import { useEffect, useMemo, useState } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    Tag,
    Space,
    message,
    Popconfirm,
    Segmented,
    Tabs,
    Switch,
    Steps,
    Avatar,
    Tooltip,
    Upload,
    Spin,
    Empty,
    Alert,
} from 'antd'
import {
    UserOutlined,
    CloudServerOutlined,
    MessageOutlined,
    TeamOutlined,
    PlusOutlined,
    EditOutlined,
    LoginOutlined,
    DeleteOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    QuestionCircleOutlined,
    GlobalOutlined,
    MobileOutlined,
    UploadOutlined,
    InboxOutlined,
    DesktopOutlined,
    SendOutlined,
    IdcardOutlined,
    SearchOutlined,
} from '@ant-design/icons'
import { statsApi, operatorsApi, accountsApi, proxiesApi } from '../services/api'
import AccountsStatistics from '../components/AccountsStatistics'
import { profileLabel, visibilityProfilesApi } from '../services/visibilityProfilesApi'
import VisibilityProfilesSettings from '../components/VisibilityProfilesSettings'
import { DEFAULT_TEMPERATURE, TEMPERATURE_OPTIONS, temperatureColor, temperatureLabel, temperatureRank } from '../constants/leadTemperature'

// ---- Таблица TG-аккаунтов: порядок и сортировка -------------------------

// Сравнение «по-человечески»: manager2 раньше manager10. Пустые - в конец.
const naturalCollator = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' })
function naturalCompare(a?: string | null, b?: string | null): number {
    const left = (a || '').trim()
    const right = (b || '').trim()
    if (!left && !right) return 0
    if (!left) return 1
    if (!right) return -1
    return naturalCollator.compare(left, right)
}

// Постоянный порядок по умолчанию: по юзернейму, затем по телефону и номеру.
// Не зависит ни от статуса, ни от активности - поэтому строки не прыгают.
function compareAccountsDefault(a: any, b: any): number {
    return naturalCompare(a.username, b.username)
        || naturalCompare(a.phone, b.phone)
        || (a.id || 0) - (b.id || 0)
}

// Тяжесть состояния для сортировки по статусу: чем меньше, тем серьёзнее.
function accountStatusRank(account: any): number {
    const health = account.health_status || ''
    if (health === 'banned_by_telegram' || health === 'hard_banned' || account.status === 'banned') return 0
    if (health === 'session_invalid' || health === 'configuration_error' || health === 'error') return 1
    if (health === 'temporarily_blocked' || health === 'limited' || account.status === 'limited') return 2
    if (!account.is_connected) return 3
    return 4
}

// Название страны по коду: IN -> «Индия».
function countryName(code?: string | null, lang: 'ru' | 'en' = 'ru'): string {
    if (!code) return ''
    try {
        return new Intl.DisplayNames([lang], { type: 'region' }).of(code.toUpperCase()) || code
    } catch {
        return code
    }
}

const ACCOUNT_TABLE_VIEW_KEY = 'orchestrator.accountsTable.v1'

interface AccountTableView {
    pageSize: number
    sortKey: string | null
    sortOrder: 'ascend' | 'descend' | null
}

function loadAccountTableView(): AccountTableView {
    const fallback: AccountTableView = { pageSize: 20, sortKey: null, sortOrder: null }
    try {
        const raw = localStorage.getItem(ACCOUNT_TABLE_VIEW_KEY)
        if (!raw) return fallback
        const parsed = JSON.parse(raw)
        return {
            pageSize: [10, 20, 50, 100].includes(parsed.pageSize) ? parsed.pageSize : fallback.pageSize,
            sortKey: typeof parsed.sortKey === 'string' ? parsed.sortKey : null,
            sortOrder: parsed.sortOrder === 'ascend' || parsed.sortOrder === 'descend' ? parsed.sortOrder : null,
        }
    } catch {
        return fallback
    }
}

const GEO_OPTIONS = [
    'Азербайджан', 'Африка', 'Бангладеш', 'Ганна', 'Гвиана', 'Египет', 'Замбия', 'Индия',
    'Кения', 'Киргизстан', 'Китай', 'Конго', 'Корея', 'Коста Рика', "Кот-д'Ивуар", 'Малави',
    'Марокко', 'Монголия', 'Непал', 'Нигерия', 'Никарагуа', 'Пакистан', 'Танзания', 'Тунис',
    'Уганда', 'Узбекистан', 'Шри-Ланка', 'Япония',
]

interface DashboardStats {
    total_accounts: number
    active_accounts: number
    banned_accounts: number
    total_operators: number
    online_operators: number
    total_messages: number
    messages_today: number
    total_contacts: number
}

export default function Admin() {
    const { user } = useAuthStore()
    const superAdminOnly = (element: any) => user?.role === 'superadmin' ? element : <Navigate to="/admin/operators" replace />

    return (
        <Routes>
            <Route path="/" element={<AdminDashboard />} />
            <Route path="/operators" element={<OperatorsPage />} />
            <Route path="/accounts" element={superAdminOnly(<AccountsPage />)} />
            <Route path="/proxies" element={superAdminOnly(<ProxiesPage />)} />
        </Routes>
    )
}

function AdminDashboard() {
    const [stats, setStats] = useState<DashboardStats | null>(null)
    const [loading, setLoading] = useState(true)
    const { user } = useAuthStore()
    const isSuperAdmin = user?.role === 'superadmin'

    useEffect(() => {
        fetchStats()
    }, [])

    const fetchStats = async () => {
        try {
            const response = await statsApi.getDashboard()
            setStats(response.data)
        } catch (error) {
            message.error('Ошибка загрузки статистики')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div style={{ padding: 24 }}>
            <h1 style={{ color: 'white', marginBottom: 24 }}>Dashboard</h1>

            <Row gutter={[16, 16]}>
                {isSuperAdmin && (
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="dashboard-card" loading={loading}>
                            <Statistic
                                title={<span style={{ color: '#888' }}>TG Аккаунты</span>}
                                value={stats?.total_accounts || 0}
                                prefix={<CloudServerOutlined style={{ color: '#1890ff' }} />}
                                suffix={
                                    <span style={{ fontSize: 14, color: '#52c41a' }}>
                                        {stats?.active_accounts || 0} активных
                                    </span>
                                }
                            />
                        </Card>
                    </Col>
                )}

                <Col xs={24} sm={12} lg={6}>
                    <Card className="dashboard-card" loading={loading}>
                        <Statistic
                            title={<span style={{ color: '#888' }}>Операторы</span>}
                            value={stats?.total_operators || 0}
                            prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
                            suffix={
                                <span style={{ fontSize: 14, color: '#52c41a' }}>
                                    {stats?.online_operators || 0} онлайн
                                </span>
                            }
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card className="dashboard-card" loading={loading}>
                        <Statistic
                            title={<span style={{ color: '#888' }}>Сообщений сегодня</span>}
                            value={stats?.messages_today || 0}
                            prefix={<MessageOutlined style={{ color: '#faad14' }} />}
                            suffix={
                                <span style={{ fontSize: 14, color: '#888' }}>
                                    всего {stats?.total_messages || 0}
                                </span>
                            }
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card className="dashboard-card" loading={loading}>
                        <Statistic
                            title={<span style={{ color: '#888' }}>Контакты</span>}
                            value={stats?.total_contacts || 0}
                            prefix={<UserOutlined style={{ color: '#13c2c2' }} />}
                        />
                    </Card>
                </Col>
            </Row>

            {stats?.banned_accounts && stats.banned_accounts > 0 && (
                <Card
                    className="dashboard-card"
                    style={{ marginTop: 16, borderColor: '#ff4d4f' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <ExclamationCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
                        <div>
                            <div style={{ color: '#ff4d4f', fontWeight: 500 }}>
                                {stats.banned_accounts} аккаунтов заблокировано
                            </div>
                            <div style={{ color: '#888', fontSize: 13 }}>
                                Проверьте прокси и замените заблокированные аккаунты
                            </div>
                        </div>
                    </div>
                </Card>
            )}
        </div>
    )
}


import { useAuthStore } from '../store/authStore'
import { DEV_ADMIN, ROLE_AVATAR_COLORS, ROLE_COLORS, managesSettings, roleLabel, roleOptions, seesAllLeads } from '../constants/roles'
import AuditLog from '../components/AuditLog'
import {
    WechatOutlined,
    SafetyCertificateOutlined,
} from '@ant-design/icons'



function OperatorsPage() {
    const { user, impersonate } = useAuthStore()
    const [operators, setOperators] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingOperator, setEditingOperator] = useState<any>(null)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [form] = Form.useForm()
    const [editForm] = Form.useForm()
    const [profileOptions, setProfileOptions] = useState<{ value: number; label: string }[]>([])



    // Профили видимости - для выбора в карточке сотрудника.
    const loadProfileOptions = () => {
        visibilityProfilesApi.list()
            .then(({ data }) => setProfileOptions(data.map((profile) => ({
                value: profile.id,
                label: `${profileLabel(profile.name, profile.wiseway_email)}${profile.is_active ? '' : ' (отключён)'}`,
            }))))
            .catch(() => setProfileOptions([]))
    }

    useEffect(() => {
        fetchOperators()
        loadProfileOptions()
    }, [])

    const fetchOperators = async () => {
        try {
            const response = await operatorsApi.getAll()
            setOperators(response.data)
        } catch (error) {
            message.error('Ошибка загрузки пользователей')
        } finally {
            setLoading(false)
        }
    }

    // Личная почта у каждого своя: занятую показываем ещё при вводе.
    const checkEmailFree = async (value: string | undefined, excludeId?: number) => {
        const email = (value || '').trim()
        if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return
        let result: { available: boolean; detail: string | null } | null = null
        try {
            result = (await operatorsApi.emailAvailable(email, excludeId)).data
        } catch {
            // Проверить не удалось - не мешаем: сервер всё равно проверит при сохранении.
            return
        }
        if (!result.available) throw new Error(result.detail || 'Такая почта уже используется - укажите другую')
    }

    const handleCreate = async (values: any) => {
        try {
            const payload = {
                ...values,
                department: values.department || null,
                wiseway_email: values.wiseway_email?.trim() || null,
            }
            await operatorsApi.create(payload)
            message.success('Пользователь создан')
            setModalOpen(false)
            form.resetFields()
            fetchOperators()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка создания')
        }
    }

    const handleDelete = async (id: number) => {
        try {
            await operatorsApi.delete(id)
            message.success('Пользователь удалён')
            fetchOperators()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка удаления')
        }
    }

    const handleEdit = (record: any) => {
        setEditingOperator(record)
        editForm.setFieldsValue({
            username: record.username,
            full_name: record.full_name,
            role: record.role,
            department: record.department || '',
            wiseway_email: record.wiseway_email || '',
            visibility_profile_ids: record.visibility_profile_ids || [],
            is_active: record.is_active,
            otp_enabled: Boolean(record.otp_enabled),
            // Пароль - всегда пустой: иначе в поле оставался пароль, введённый
            // при прошлом редактировании другого сотрудника, и молча уходил ему.
            password: '',
        })
        setEditModalOpen(true)
    }

    const handleUpdate = async (values: any) => {
        try {

            const dataToUpdate = { ...values }
            if (!dataToUpdate.password) {
                delete dataToUpdate.password
            }
            // Вход по коду меняет только «Админ разработки» - остальные его не шлют.
            if (user?.role !== DEV_ADMIN) {
                delete dataToUpdate.otp_enabled
            }
            if (dataToUpdate.department === undefined || dataToUpdate.department === '') {
                dataToUpdate.department = null
            }
            // Пустую строку сервер не примет как почту, поэтому очищаем полем null.
            if ('wiseway_email' in dataToUpdate) {
                dataToUpdate.wiseway_email = dataToUpdate.wiseway_email?.trim() || null
            }

            await operatorsApi.update(editingOperator.id, dataToUpdate)
            message.success(dataToUpdate.password
                ? 'Пользователь обновлён. Пароль изменён — сообщите сотруднику новый пароль'
                : 'Пользователь обновлён')
            setEditModalOpen(false)
            editForm.resetFields()
            fetchOperators()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка обновления')
        }
    }

    const isAdmin = seesAllLeads(user?.role)
    const roleColor = (role: string) => ROLE_COLORS[role] || 'default'

    const columns = [
        {
            title: '',
            key: 'avatar',
            width: 60,
            render: (_: any, record: any) => (
                <Avatar
                    src={record.avatar_url}
                    style={{ backgroundColor: ROLE_AVATAR_COLORS[record.role] || '#1890ff' }}
                >
                    {(record.full_name || record.username || 'U')[0].toUpperCase()}
                </Avatar>
            ),
        },
        {
            title: 'Логин',
            dataIndex: 'username',
            key: 'username',
        },
        {
            title: 'Имя',
            dataIndex: 'full_name',
            key: 'full_name',
        },
        {
            title: 'Роль',
            dataIndex: 'role',
            key: 'role',
            render: (role: string) => (
                <Tag color={roleColor(role)}>{roleLabel(role)}</Tag>
            ),
        },
        {
            title: 'Отдел',
            dataIndex: 'department',
            key: 'department',
            render: (department: string | null) => department ? <Tag color={department === 'AM' ? 'cyan' : 'blue'}>{department}</Tag> : '-',
        },
        {
            title: 'Профили видимости',
            dataIndex: 'visibility_profile_names',
            key: 'visibility_profile_names',
            render: (names: string[] | undefined) => names?.length
                ? names.map((name) => <Tag key={name}>{name}</Tag>)
                : '-',
        },
        {
            title: 'Личная почта для OTP',
            dataIndex: 'wiseway_email',
            key: 'wiseway_email',
            render: (email: string | null) => email || '-',
        },
    ]


    if (isAdmin) {
        columns.push(
            {
                title: 'Статус',
                dataIndex: 'is_active',
                key: 'is_active',
                render: (active: boolean) => (
                    <Tag color={active ? 'success' : 'error'}>
                        {active ? 'Активен' : 'Отключен'}
                    </Tag>
                ),
            } as any,
            {
                title: 'Вход по коду',
                dataIndex: 'otp_enabled',
                key: 'otp_enabled',
                render: (enabled: boolean) => (
                    <Tag color={enabled ? 'processing' : 'default'}>{enabled ? 'Включён' : 'Выключен'}</Tag>
                ),
            } as any,
            {
                title: 'Аккаунты',
                dataIndex: 'accounts_count',
                key: 'accounts_count',
            } as any,
            {
                title: 'Сообщ. сегодня',
                dataIndex: 'messages_today',
                key: 'messages_today',
            } as any
        )
    }


    columns.push({
        title: 'Действия',
        key: 'actions',
        render: (_: any, record: any) => (
            <Space>
                {/* Вход под сотрудником - только «Админу разработки»: посмотреть
                    и сделать то же, что сотрудник, его глазами. */}
                {user?.role === DEV_ADMIN && record.role !== DEV_ADMIN && record.is_active && record.id !== user?.id && (
                    <Popconfirm
                        title={`Войти как ${record.full_name || record.username}?`}
                        description="Все действия пойдут от имени сотрудника. Вернуться — кнопкой на плашке сверху."
                        okText="Войти"
                        cancelText="Отмена"
                        onConfirm={() => impersonate(record.id).catch((error: any) => {
                            message.error(error.response?.data?.detail || 'Не удалось войти под сотрудником')
                        })}
                    >
                        <Tooltip title="Войти как">
                            <Button type="text" icon={<LoginOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                )}
                {isAdmin && (
                    <>
                        <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                        <Popconfirm
                            title="Удалить пользователя?"
                            onConfirm={() => handleDelete(record.id)}
                        >
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </>
                )}
            </Space>
        ),
    } as any)

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h1 style={{ color: 'white', margin: 0 }}>Пользователи</h1>
                {isAdmin && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setModalOpen(true)}
                    >
                        Добавить
                    </Button>
                )}
            </div>

            <Table
                dataSource={operators}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1120 }}
            />

            <Modal
                title="Новый пользователь"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
            >
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item
                        name="username"
                        label="Логин"
                        rules={[{ required: true, message: 'Введите логин' }]}
                    >
                        <Input autoComplete="off" />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Пароль"
                        rules={[{ required: true, message: 'Введите пароль' }]}
                    >
                        <Input.Password autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item name="full_name" label="Имя">
                        <Input />
                    </Form.Item>
                    <Form.Item name="role" label="Роль" initialValue="operator">
                        <Select options={roleOptions(user?.role === DEV_ADMIN)} />
                    </Form.Item>
                    <Form.Item name="department" label="Отдел" initialValue="">
                        <Select>
                            <Select.Option value="">Не выбрано</Select.Option>
                            <Select.Option value="LPE">LPE</Select.Option>
                            <Select.Option value="AM">AM</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="visibility_profile_ids"
                        label="Профили видимости"
                        extra="Команды, лидов которых сотрудник видит и от почты которых пишет в CRM Wiseway. При добавлении в профиль лиды сотрудника без профиля переходят в него. Профили заводятся ниже, в блоке «Профили видимости»."
                    >
                        <Select
                            mode="multiple"
                            options={profileOptions}
                            optionFilterProp="label"
                            placeholder="Без профиля"
                            allowClear
                        />
                    </Form.Item>
                    <Form.Item
                        name="wiseway_email"
                        label="Личная почта для OTP"
                        extra="На неё приходят коды входа. Если у сотрудника нет профиля видимости, от неё же идёт работа с CRM Wiseway."
                        validateDebounce={400}
                        rules={[
                            { type: 'email', message: 'Введите корректную почту' },
                            { validator: (_, value) => checkEmailFree(value, undefined) },
                        ]}
                    >
                        <Input placeholder="name@wiseway.com" autoComplete="off" />
                    </Form.Item>
                    {/* Вход по коду включает и выключает только «Админ разработки». */}
                    {user?.role === DEV_ADMIN && (
                        <Form.Item
                            name="otp_enabled"
                            label="Вход по коду"
                            valuePropName="checked"
                            extra="После пароля на личную почту для OTP приходит код. В одном браузере код спрашивается раз в сутки."
                        >
                            <Switch />
                        </Form.Item>
                    )}
                </Form>
            </Modal>


            <Modal
                title="Редактировать пользователя"
                open={editModalOpen}
                onCancel={() => {
                    setEditModalOpen(false)
                    editForm.setFieldValue('password', '')
                }}
                onOk={() => editForm.submit()}
            >
                <Form form={editForm} onFinish={handleUpdate} layout="vertical">
                    <Form.Item
                        name="username"
                        label="Логин"
                        rules={[{ required: true, message: 'Введите логин' }]}
                    >
                        <Input autoComplete="off" />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Новый пароль"
                        help="Оставьте пустым, если не хотите менять пароль"
                    >
                        {/* Браузер не должен сам подставлять сюда сохранённый пароль. */}
                        <Input.Password placeholder="Новый пароль" autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item name="full_name" label="Имя">
                        <Input />
                    </Form.Item>
                    <Form.Item name="role" label="Роль">
                        <Select options={roleOptions(user?.role === DEV_ADMIN)} />
                    </Form.Item>
                    <Form.Item name="department" label="Отдел">
                        <Select>
                            <Select.Option value="">Не выбрано</Select.Option>
                            <Select.Option value="LPE">LPE</Select.Option>
                            <Select.Option value="AM">AM</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="visibility_profile_ids"
                        label="Профили видимости"
                        extra="Команды, лидов которых сотрудник видит и от почты которых пишет в CRM Wiseway. При добавлении в профиль лиды сотрудника без профиля переходят в него. Профили заводятся ниже, в блоке «Профили видимости»."
                    >
                        <Select
                            mode="multiple"
                            options={profileOptions}
                            optionFilterProp="label"
                            placeholder="Без профиля"
                            allowClear
                        />
                    </Form.Item>
                    <Form.Item
                        name="wiseway_email"
                        label="Личная почта для OTP"
                        extra="На неё приходят коды входа. Если у сотрудника нет профиля видимости, от неё же идёт работа с CRM Wiseway."
                        validateDebounce={400}
                        rules={[
                            { type: 'email', message: 'Введите корректную почту' },
                            { validator: (_, value) => checkEmailFree(value, editingOperator?.id) },
                        ]}
                    >
                        <Input placeholder="name@wiseway.com" autoComplete="off" />
                    </Form.Item>
                    {/* Вход по коду включает и выключает только «Админ разработки». */}
                    {user?.role === DEV_ADMIN && (
                        <Form.Item
                            name="otp_enabled"
                            label="Вход по коду"
                            valuePropName="checked"
                            extra="После пароля на личную почту для OTP приходит код. В одном браузере код спрашивается раз в сутки."
                        >
                            <Switch />
                        </Form.Item>
                    )}
                    <Form.Item name="is_active" label="Активен" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Профили видимости - рядом с сотрудниками: их раздают учётным
                записям, и состав команд правят там же, где заводят людей. */}
            {managesSettings(user?.role) && (
                <Card title="Профили видимости" style={{ marginBottom: 24 }}>
                    <VisibilityProfilesSettings onChanged={() => { fetchOperators(); loadProfileOptions() }} />
                </Card>
            )}

            {/* Журнал стоит здесь же: смена ролей - это действие над учётной
                записью, и искать его логично там, где эти записи и заводят. */}
            {managesSettings(user?.role) && <AuditLog />}

        </div>
    )
}

function AccountsPage() {
    // Подразделы: список аккаунтов и статистика их работы.
    const [accountsTab, setAccountsTab] = useState<'list' | 'stats'>('list')
    const [accounts, setAccounts] = useState<any[]>([])
    const [operators, setOperators] = useState<any[]>([])
    const [proxies, setProxies] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [tdataModalOpen, setTdataModalOpen] = useState(false)
    const [newTdataModalOpen, setNewTdataModalOpen] = useState(false)
    const [bulkTdataModalOpen, setBulkTdataModalOpen] = useState(false)
    const [bulkTdataGeo, setBulkTdataGeo] = useState<string>()
    const [selectedAccount, setSelectedAccount] = useState<any>(null)
    const [uploading, setUploading] = useState(false)
    const [tdataFile, setTdataFile] = useState<File | null>(null)
    const [newTdataFile, setNewTdataFile] = useState<File | null>(null)
    const [bulkTdataFiles, setBulkTdataFiles] = useState<File[]>([])
    const [tdataArchivePassword, setTdataArchivePassword] = useState('')
    const [newTdataArchivePassword, setNewTdataArchivePassword] = useState('')
    const [bulkTdataArchivePassword, setBulkTdataArchivePassword] = useState('')
    const [refreshingStatuses, setRefreshingStatuses] = useState(false)
    const [form] = Form.useForm()
    const [editForm] = Form.useForm()
    const [qrAuthForm] = Form.useForm()
    const [phoneAuthForm] = Form.useForm()
    const [profileModalOpen, setProfileModalOpen] = useState(false)
    const [profileForm] = Form.useForm()
    const [profileAvatarFile, setProfileAvatarFile] = useState<File | null>(null)
    const [profileRemoveAvatar, setProfileRemoveAvatar] = useState(false)
    const [profileSaving, setProfileSaving] = useState(false)
    // Облачный пароль (2FA) редактируемого аккаунта.
    const [cloudPwdOpen, setCloudPwdOpen] = useState(false)
    const [cloudPwdForm] = Form.useForm()
    const [cloudPwdSaving, setCloudPwdSaving] = useState(false)
    const [cloudPwdState, setCloudPwdState] = useState<import('../services/api').CloudPasswordState | null>(null)
    const [cloudPwdStateLoading, setCloudPwdStateLoading] = useState(false)
    const { user } = useAuthStore()
    const isTeamLead = seesAllLeads(user?.role)
    const [selectedPoolGeo, setSelectedPoolGeo] = useState<string | null>(null)
    const accountPoolStats = useMemo(() => (
        GEO_OPTIONS.map((geo) => ({
            geo,
            total: accounts.filter((account) => account.pool_geo === geo).length,
            active: accounts.filter((account) => account.pool_geo === geo && account.status === 'active').length,
            connected: accounts.filter((account) => account.pool_geo === geo && account.is_connected).length,
        }))
    ), [accounts])
    const [selectedPoolTemperature, setSelectedPoolTemperature] = useState<string | null>(null)
    const accountTemperatureStats = useMemo(() => (
        TEMPERATURE_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
            total: accounts.filter((account) => account.pool_temperature === option.value).length,
            connected: accounts.filter((account) => account.pool_temperature === option.value && account.is_connected).length,
        }))
    ), [accounts])
    // Поиск по юзернейму и телефону над таблицей аккаунтов.
    const [accountSearch, setAccountSearch] = useState('')
    // Проверка внешнего адреса: какие аккаунты сейчас проверяются и прогресс «проверить все».
    const [exitChecking, setExitChecking] = useState<Set<number>>(new Set())
    const [exitCheckAll, setExitCheckAll] = useState<{ done: number; total: number } | null>(null)

    const checkExitIp = async (accountId: number, silent = false) => {
        setExitChecking((current) => new Set(current).add(accountId))
        try {
            const response = await accountsApi.checkExitIp(accountId)
            setAccounts((current) => current.map((item) => (item.id === accountId ? { ...item, ...response.data } : item)))
            setSessionsAccount((current: any) => (current && current.id === accountId ? { ...current, ...response.data } : current))
            if (!silent) {
                if (response.data.exit_error) message.warning(response.data.exit_error, 6)
                else message.success(`Внешний IP: ${response.data.exit_ip}`)
            }
        } catch (error: any) {
            if (!silent) message.error(error.response?.data?.detail || 'Не удалось проверить внешний IP')
        } finally {
            setExitChecking((current) => {
                const next = new Set(current)
                next.delete(accountId)
                return next
            })
        }
    }

    // Проверяем по очереди, а не разом: каждый запрос идёт через прокси, и
    // двадцать одновременных подключений ни к чему.
    const checkExitIpAll = async (ids: number[]) => {
        if (!ids.length || exitCheckAll) return
        setExitCheckAll({ done: 0, total: ids.length })
        for (let index = 0; index < ids.length; index += 1) {
            await checkExitIp(ids[index], true)
            setExitCheckAll({ done: index + 1, total: ids.length })
        }
        setExitCheckAll(null)
        message.success('Проверка внешних IP завершена')
    }
    // Сортировка и размер страницы запоминаются в браузере, чтобы после
    // обновления страницы таблица выглядела так же.
    const [accountTableView, setAccountTableView] = useState<AccountTableView>(loadAccountTableView)
    useEffect(() => {
        try {
            localStorage.setItem(ACCOUNT_TABLE_VIEW_KEY, JSON.stringify(accountTableView))
        } catch {
            // приватный режим или запрет хранилища - просто не запоминаем
        }
    }, [accountTableView])

    const filteredAccounts = useMemo(() => {
        const query = accountSearch.trim().toLowerCase().replace(/^@/, '')
        return accounts
            .filter((account) => (
                (!selectedPoolGeo || account.pool_geo === selectedPoolGeo) &&
                (!selectedPoolTemperature || account.pool_temperature === selectedPoolTemperature) &&
                (!query
                    || (account.username || '').toLowerCase().includes(query)
                    || (account.phone || '').toLowerCase().includes(query))
            ))
            // Постоянный порядок по умолчанию: аккаунт стоит на своём месте, что
            // бы с ним ни происходило. Сортировка по столбцу - только по клику.
            .sort(compareAccountsDefault)
    }, [accounts, selectedPoolGeo, selectedPoolTemperature, accountSearch])


    const [authModalOpen, setAuthModalOpen] = useState(false)
    const [authStep, setAuthStep] = useState<'qr' | '2fa'>('qr')
    const [authLoading, setAuthLoading] = useState(false)
    const [authAccountId, setAuthAccountId] = useState<number | null>(null)
    const [qrUrl, setQrUrl] = useState<string>('')
    const [qrPollingRef] = useState<{ current: ReturnType<typeof setInterval> | null }>({ current: null })


    const [phoneAuthModalOpen, setPhoneAuthModalOpen] = useState(false)
    const [phoneAuthStep, setPhoneAuthStep] = useState<'phone_input' | 'verify_code' | '2fa'>('phone_input')
    const [phoneAuthPhone, setPhoneAuthPhone] = useState('')
    const [phoneAuthHash, setPhoneAuthHash] = useState('')
    const [phoneAuthAccountId, setPhoneAuthAccountId] = useState<number | null>(null)


    const [proxyCheckResult, setProxyCheckResult] = useState<any>(null)
    const [proxyCheckModalOpen, setProxyCheckModalOpen] = useState(false)
    const [sessionsModalOpen, setSessionsModalOpen] = useState(false)
    const [sessionsAccount, setSessionsAccount] = useState<any>(null)
    const [accountSessions, setAccountSessions] = useState<any[]>([])
    const [sessionsLoading, setSessionsLoading] = useState(false)
    const [sessionsActionLoading, setSessionsActionLoading] = useState(false)
    const [chatsModalOpen, setChatsModalOpen] = useState(false)
    const [chatsAccount, setChatsAccount] = useState<any>(null)
    const [accountDialogs, setAccountDialogs] = useState<any[]>([])
    const [accountDialogsLoading, setAccountDialogsLoading] = useState(false)
    const [accountDialogsHasMore, setAccountDialogsHasMore] = useState(false)
    const [accountDialogsNextOffset, setAccountDialogsNextOffset] = useState<any>(null)
    const [selectedAccountDialog, setSelectedAccountDialog] = useState<any>(null)
    const [accountDialogMessages, setAccountDialogMessages] = useState<any[]>([])
    const [accountDialogMessagesLoading, setAccountDialogMessagesLoading] = useState(false)
    const [accountDialogText, setAccountDialogText] = useState('')
    const [accountDialogSending, setAccountDialogSending] = useState(false)
    const [deletingChatId, setDeletingChatId] = useState<number | null>(null)

    useEffect(() => {
        fetchAccounts()
        fetchOperators()
        fetchProxies()


        const token = localStorage.getItem('token')
        if (!token) return

        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const wsUrl = `${wsProtocol}//${window.location.host}/ws?token=${token}`

        let ws: WebSocket | null = null
        let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
        let reconnectDelay = 1000
        let isClosed = false

        const connect = () => {
            if (isClosed) return
            ws = new WebSocket(wsUrl)
            ws.onopen = () => { reconnectDelay = 1000 }
            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    if (data.type === 'account_update' && data.account) {
                        setAccounts(prev => prev.map(acc =>
                            acc.id === data.account.id ? { ...acc, ...data.account } : acc
                        ))
                    }
                } catch (e) {
                    console.error('WebSocket parse error:', e)
                }
            }
            ws.onclose = () => {
                if (!isClosed) {
                    reconnectTimeout = setTimeout(connect, reconnectDelay)
                    reconnectDelay = Math.min(reconnectDelay * 1.5, 10000)
                }
            }
        }

        connect()

        return () => {
            isClosed = true
            if (reconnectTimeout) clearTimeout(reconnectTimeout)
            if (ws) ws.close()
        }
    }, [])

    const dedupeAccountDialogs = (items: any[]) => {
        const unique = new Map<number, any>()
        items.forEach((item) => unique.set(Number(item.db_chat_id || item.chat_id), item))
        return Array.from(unique.values())
    }

    // Счетчики диалогов считаются одним SQL-запросом на backend вместо
    // постраничной выгрузки всех диалогов каждого аккаунта из Telegram.
    const syncDialogCounts = async () => {
        try {
            const response = await accountsApi.getDialogCounts()
            const counts = response.data.counts || {}
            setAccounts((current) => current.map((item) => (
                counts[item.id] !== undefined ? { ...item, chats_count: counts[item.id] } : item
            )))
        } catch {
            return
        }
    }

    const fetchAccounts = async () => {
        try {
            const response = await accountsApi.getAll(0, 500, true)
            setAccounts(response.data)
            void syncDialogCounts()
        } catch (error) {
            message.error('Ошибка загрузки аккаунтов')
        } finally {
            setLoading(false)
        }
    }

    const handleRefreshStatuses = async () => {
        setRefreshingStatuses(true)
        try {
            const response = await accountsApi.refreshStatuses()
            message.info(`Проверка ${response.data.accounts_count} аккаунтов запущена`)
            window.setTimeout(fetchAccounts, 5000)
            window.setTimeout(fetchAccounts, 15000)
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка актуализации статусов')
        } finally {
            window.setTimeout(() => setRefreshingStatuses(false), 5000)
        }
    }

    const fetchOperators = async () => {
        try {
            const response = await operatorsApi.getAll()
            setOperators(response.data)
        } catch (error) {
            console.error('Error fetching operators:', error)
        }
    }

    const fetchProxies = async () => {
        try {
            const response = await proxiesApi.getAll()
            setProxies(response.data)
        } catch (error) {
            console.error('Error fetching proxies:', error)
        }
    }

    const handleCreate = async (values: any) => {
        try {
            await accountsApi.create({ ...values, pool_geo: values.pool_geo || null, pool_temperature: values.pool_temperature || DEFAULT_TEMPERATURE })
            message.success('Аккаунт создан')
            setModalOpen(false)
            form.resetFields()
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка создания')
        }
    }


    const handleStartQrAuth = async (values: { proxy_id?: number; pool_geo: string; pool_temperature?: string }) => {
        setAuthLoading(true)
        try {
            const response = await accountsApi.startQrLogin(values.proxy_id, values.pool_geo, values.pool_temperature || DEFAULT_TEMPERATURE)
            setAuthAccountId(response.data.account_id)
            setQrUrl(response.data.qr_url)
            setAuthStep('qr')


            startQrPolling(response.data.account_id, values)
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка генерации QR')
        } finally {
            setAuthLoading(false)
        }
    }

    const startQrPolling = (
        accountId: number,
        authConfig: { proxy_id?: number; pool_geo: string; pool_temperature?: string }
    ) => {
        if (qrPollingRef.current) clearInterval(qrPollingRef.current)

        qrPollingRef.current = setInterval(async () => {
            try {
                const response = await accountsApi.checkQrLogin(accountId)
                const { status: loginStatus } = response.data

                if (loginStatus === 'success') {
                    stopQrPolling()
                    message.success('Авторизация успешна!')
                    handleAuthComplete()
                } else if (loginStatus === '2fa_required') {
                    stopQrPolling()
                    setAuthStep('2fa')
                    message.info('Требуется пароль 2FA')
                } else if (loginStatus === 'expired') {
                    stopQrPolling()
                    message.warning('QR код истёк. Генерируем новый...')
                    handleStartQrAuth(authConfig)
                } else if (loginStatus === 'waiting' && response.data.qr_url) {
                    setQrUrl(response.data.qr_url)
                }
            } catch (error) {
                console.error('QR poll error:', error)
            }
        }, 3000)
    }

    const stopQrPolling = () => {
        if (qrPollingRef.current) {
            clearInterval(qrPollingRef.current)
            qrPollingRef.current = null
        }
    }

    const handleVerify2fa = async (password: string) => {
        if (!authAccountId) return
        setAuthLoading(true)
        try {
            await accountsApi.qr2fa(authAccountId, password)
            message.success('Авторизация успешна!')
            handleAuthComplete()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Неверный пароль 2FA')
        } finally {
            setAuthLoading(false)
        }
    }

    const handleAuthComplete = () => {
        stopQrPolling()
        setAuthModalOpen(false)
        setAuthStep('qr')
        setAuthAccountId(null)
        setQrUrl('')
        qrAuthForm.resetFields()
        fetchAccounts()
    }

    const handleAuthCancel = () => {
        stopQrPolling()
        setAuthModalOpen(false)
        setAuthStep('qr')
        setAuthAccountId(null)
        setQrUrl('')
        qrAuthForm.resetFields()
    }


    const handleCreateAndSendCode = async (values: any) => {
        setAuthLoading(true)
        try {

            const createResponse = await accountsApi.create({
                phone: values.phone,
                proxy_id: values.proxy_id,
                pool_geo: values.pool_geo,
                pool_temperature: values.pool_temperature || DEFAULT_TEMPERATURE,
            })
            const newAccountId = createResponse.data.id
            setPhoneAuthAccountId(newAccountId)
            setPhoneAuthPhone(values.phone)


            const sendResponse = await accountsApi.sendCode(newAccountId)
            setPhoneAuthHash(sendResponse.data.phone_code_hash)
            setPhoneAuthStep('verify_code')
            message.success('Код отправлен')
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка создания аккаунта или отправки кода')
        } finally {
            setAuthLoading(false)
        }
    }

    const handleResendPhoneCode = async () => {
        if (!phoneAuthAccountId || !phoneAuthHash) return
        setAuthLoading(true)
        try {
            const response = await accountsApi.resendCode(phoneAuthAccountId, phoneAuthHash)
            setPhoneAuthHash(response.data.phone_code_hash)
            message.success('Код отправлен повторно (по SMS)')
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка повторной отправки кода')
        } finally {
            setAuthLoading(false)
        }
    }

    const handleVerifyPhoneCode = async (values: any) => {
        if (!phoneAuthAccountId) return
        setAuthLoading(true)
        try {
            const response = await accountsApi.verifyCode(phoneAuthAccountId, values.code, phoneAuthHash)
            if (response.data.status === '2fa_required') {
                setPhoneAuthStep('2fa')
                message.info('Требуется пароль 2FA')
            } else {
                message.success('Авторизация успешна!')
                handlePhoneAuthComplete()
            }
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Неверный код')
        } finally {
            setAuthLoading(false)
        }
    }

    const handleVerifyPhone2fa = async (values: any) => {
        if (!phoneAuthAccountId) return
        setAuthLoading(true)
        try {
            await accountsApi.verify2fa(phoneAuthAccountId, values.password)
            message.success('Авторизация успешна!')
            handlePhoneAuthComplete()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Неверный пароль 2FA')
        } finally {
            setAuthLoading(false)
        }
    }

    const handlePhoneAuthComplete = () => {
        setPhoneAuthModalOpen(false)
        setPhoneAuthStep('phone_input')
        setPhoneAuthAccountId(null)
        setPhoneAuthHash('')
        setPhoneAuthPhone('')
        phoneAuthForm.resetFields()
        fetchAccounts()
    }

    const handlePhoneAuthCancel = () => {
        setPhoneAuthModalOpen(false)
        setPhoneAuthStep('phone_input')
        setPhoneAuthAccountId(null)
        setPhoneAuthHash('')
        setPhoneAuthPhone('')
        phoneAuthForm.resetFields()
    }

    const handleEdit = async (values: any) => {
        if (!selectedAccount) return
        try {
            await accountsApi.update(selectedAccount.id, {
                ...values,
                // Явно шлём null при очистке прокси: undefined выпал бы из JSON,
                // и сервер не понял бы, что прокси надо снять.
                proxy_id: values.proxy_id ?? null,
                pool_geo: values.pool_geo || null,
                pool_temperature: values.pool_temperature || DEFAULT_TEMPERATURE,
                pool_weight: values.pool_weight || 100,
                daily_send_limit: values.daily_send_limit ?? null,
            })
            message.success('Аккаунт обновлён')
            setEditModalOpen(false)
            editForm.resetFields()
            setSelectedAccount(null)
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка обновления')
        }
    }

    const handleDelete = async (id: number) => {
        try {
            await accountsApi.delete(id)
            message.success('Аккаунт удалён')
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка удаления')
        }
    }

    const handleConnect = async (id: number) => {
        try {
            await accountsApi.connect(id)
            message.success('Подключение запущено')
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка подключения')
        }
    }

    const handleDisconnect = async (id: number) => {
        try {
            await accountsApi.disconnect(id)
            message.success('Аккаунт отключен')
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка отключения')
        }
    }

    const normalizeArchivePassword = (value: string) => value.trim() || undefined

    const isZipFile = (file: File) => {
        if (file.name.toLowerCase().endsWith('.zip')) return true
        message.error('Загрузите ZIP архив')
        return false
    }

    const resetTdataUploadState = () => {
        setTdataFile(null)
        setNewTdataFile(null)
        setBulkTdataFiles([])
        setTdataArchivePassword('')
        setNewTdataArchivePassword('')
        setBulkTdataArchivePassword('')
    }

    const handleImportTdata = async () => {
        if (!selectedAccount || !tdataFile) {
            message.warning('Выберите ZIP архив TDATA или metadata')
            return
        }
        setUploading(true)
        try {
            await accountsApi.importTdata(selectedAccount.id, tdataFile, normalizeArchivePassword(tdataArchivePassword))
            message.success('TDATA/metadata загружен')
            setTdataModalOpen(false)
            setSelectedAccount(null)
            resetTdataUploadState()
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка загрузки TDATA/metadata')
        } finally {
            setUploading(false)
        }
    }

    const handleCreateFromTdata = async () => {
        if (!newTdataFile) {
            message.warning('Выберите ZIP архив TDATA или metadata')
            return
        }
        setUploading(true)
        try {
            const response = await accountsApi.createFromTdata(newTdataFile, undefined, undefined, bulkTdataGeo, normalizeArchivePassword(newTdataArchivePassword))
            message.success(`Аккаунт подключен: ${response.data.phone || response.data.username}`)
            setNewTdataModalOpen(false)
            resetTdataUploadState()
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка подключения из TDATA/metadata')
        } finally {
            setUploading(false)
        }
    }

    const handleBulkCreateFromTdata = async () => {
        if (!bulkTdataFiles.length) {
            message.warning('Выберите ZIP архивы TDATA или metadata')
            return
        }
        setUploading(true)
        try {
            const response = await accountsApi.bulkCreateFromTdata(bulkTdataFiles, bulkTdataGeo, undefined, normalizeArchivePassword(bulkTdataArchivePassword))
            message.success(`Подключено: ${response.data.created}, ошибок: ${response.data.failed}`)
            setBulkTdataModalOpen(false)
            setBulkTdataGeo(undefined)
            resetTdataUploadState()
            fetchAccounts()
            fetchProxies()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка массовой загрузки TDATA/metadata')
        } finally {
            setUploading(false)
        }
    }

    const [modal, contextHolder] = Modal.useModal();


    const fetchAccountSessions = async (accountId?: number) => {
        const targetId = accountId || sessionsAccount?.id
        if (!targetId) return
        setSessionsLoading(true)
        try {
            const response = await accountsApi.getSessions(targetId)
            setAccountSessions(response.data.sessions || [])
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка загрузки Telegram-сессий')
        } finally {
            setSessionsLoading(false)
        }
    }

    const openSessionsModal = async (record: any) => {
        setSessionsAccount(record)
        setAccountSessions([])
        setSessionsModalOpen(true)
        await fetchAccountSessions(record.id)
    }

    const closeSessionsModal = () => {
        setSessionsModalOpen(false)
        setSessionsAccount(null)
        setAccountSessions([])
    }

    const handleRevokeSession = async (sessionHash: string) => {
        if (!sessionsAccount) return
        setSessionsActionLoading(true)
        try {
            await accountsApi.revokeSession(sessionsAccount.id, sessionHash)
            message.success('Telegram-сессия завершена')
            await fetchAccountSessions(sessionsAccount.id)
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка завершения Telegram-сессии')
        } finally {
            setSessionsActionLoading(false)
        }
    }

    const handleRevokeOtherSessions = async () => {
        if (!sessionsAccount) return
        setSessionsActionLoading(true)
        try {
            const response = await accountsApi.revokeOtherSessions(sessionsAccount.id)
            message.success(response.data.message || 'Другие Telegram-сессии завершены')
            await fetchAccountSessions(sessionsAccount.id)
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка завершения других Telegram-сессий')
        } finally {
            setSessionsActionLoading(false)
        }
    }

    const formatSessionDate = (value?: string) => {
        if (!value) return '-'
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return value
        return date.toLocaleString('ru-RU')
    }

    const accountDialogTitle = (dialog: any) =>
        dialog?.name || dialog?.title || dialog?.username || dialog?.chat_id || 'Чат'

    const messagePreview = (item: any) => {
        if (item.text) return item.text
        if (item.media_file_name) return item.media_file_name
        const labels: Record<string, string> = {
            photo: 'Фото',
            video: 'Видео',
            video_note: 'Видеосообщение',
            voice: 'Голосовое сообщение',
            audio: 'Аудио',
            document: 'Документ',
            sticker: 'Стикер',
        }
        return labels[item.content_type] || item.content_type || 'Сообщение'
    }

    const fetchAccountDialogs = async (record?: any, options?: { live?: boolean }) => {
        const account = record || chatsAccount
        if (!account) return
        setAccountDialogsLoading(true)
        try {
            // Первая страница из БД; live=true (кнопка "Обновить") запрашивает
            // свежий список из Telegram, остальное догружается по кнопке.
            const response = await accountsApi.getDialogs(account.id, 100, undefined, 0, 0, undefined, options?.live)
            const dialogs = response.data.dialogs || []
            setAccountDialogs(dedupeAccountDialogs(dialogs))
            setAccountDialogsHasMore(Boolean(response.data.has_more && response.data.next_offset))
            setAccountDialogsNextOffset(response.data.next_offset || null)
            void syncDialogCounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка загрузки чатов аккаунта')
        } finally {
            setAccountDialogsLoading(false)
        }
    }

    const loadMoreAccountDialogs = async () => {
        if (!chatsAccount || !accountDialogsNextOffset) return
        setAccountDialogsLoading(true)
        try {
            const offsetDate = typeof accountDialogsNextOffset?.offset_date === 'number' ? accountDialogsNextOffset.offset_date : 0
            const response = await accountsApi.getDialogs(
                chatsAccount.id,
                100,
                undefined,
                offsetDate,
                accountDialogsNextOffset?.offset_id || 0,
                accountDialogsNextOffset?.offset_peer_id
            )
            const dialogs = response.data.dialogs || []
            setAccountDialogs((current) => dedupeAccountDialogs([...current, ...dialogs]))
            setAccountDialogsHasMore(Boolean(response.data.has_more && response.data.next_offset))
            setAccountDialogsNextOffset(response.data.next_offset || null)
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка загрузки чатов аккаунта')
        } finally {
            setAccountDialogsLoading(false)
        }
    }

    const openAccountDialog = async (dialog: any) => {
        if (!chatsAccount) return
        setSelectedAccountDialog(dialog)
        setAccountDialogMessages([])
        setAccountDialogText('')
        setAccountDialogMessagesLoading(true)
        try {
            const chatId = Number(dialog.chat_id || dialog.id)
            const response = await accountsApi.getMessages(chatsAccount.id, chatId, 200, 0)
            setAccountDialogMessages(response.data.messages || [])
        } catch (error: any) {
            message.warning(error.response?.data?.detail || 'Переписку не удалось открыть. Чат можно удалить из списка.')
        } finally {
            setAccountDialogMessagesLoading(false)
        }
    }

    const openChatsModal = async (record: any) => {
        setChatsAccount(record)
        setAccountDialogs([])
        setAccountDialogsHasMore(false)
        setAccountDialogsNextOffset(null)
        setSelectedAccountDialog(null)
        setAccountDialogMessages([])
        setAccountDialogText('')
        setChatsModalOpen(true)
        await fetchAccountDialogs(record)
    }

    const closeChatsModal = () => {
        setChatsModalOpen(false)
        setChatsAccount(null)
        setAccountDialogs([])
        setAccountDialogsHasMore(false)
        setAccountDialogsNextOffset(null)
        setSelectedAccountDialog(null)
        setAccountDialogMessages([])
        setAccountDialogText('')
    }

    const handleSendAccountDialogMessage = async () => {
        if (!chatsAccount || !selectedAccountDialog || !accountDialogText.trim()) return
        const chatId = Number(selectedAccountDialog.chat_id || selectedAccountDialog.id)
        if (!chatId) {
            message.error('Не удалось определить чат для отправки')
            return
        }
        const text = accountDialogText.trim()
        setAccountDialogSending(true)
        try {
            await accountsApi.sendMessage(chatsAccount.id, chatId, text)
            setAccountDialogText('')
            const response = await accountsApi.getMessages(chatsAccount.id, chatId, 200, 0)
            setAccountDialogMessages(response.data.messages || [])
            await fetchAccountDialogs(chatsAccount)
            message.success('Сообщение отправлено')
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка отправки сообщения')
        } finally {
            setAccountDialogSending(false)
        }
    }

    const handleDeleteAccountDialog = async (dialog: any) => {
        if (!chatsAccount) return
        const chatKey = Number(dialog.db_chat_id || dialog.chat_id)
        setDeletingChatId(chatKey)
        try {
            const response = await accountsApi.deleteChat(chatsAccount.id, chatKey, true)
            setAccountDialogs((items) => {
                const nextItems = items.filter((item) => Number(item.db_chat_id || item.chat_id) !== chatKey)
                setAccounts((current) => current.map((account) => (
                    account.id === chatsAccount.id ? { ...account, chats_count: nextItems.length } : account
                )))
                return nextItems
            })
            if (selectedAccountDialog && Number(selectedAccountDialog.db_chat_id || selectedAccountDialog.chat_id) === chatKey) {
                setSelectedAccountDialog(null)
                setAccountDialogMessages([])
            }
            if (response.data?.telegram_deleted) {
                message.success('Чат удален из CRM и Telegram у нас и у собеседника')
            } else {
                message.warning('Чат удален из CRM. Telegram не подтвердил удаление у собеседника.')
            }
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка удаления чата')
        } finally {
            setDeletingChatId(null)
        }
    }

    const handleCheckProxy = async (id: number) => {
        const hide = message.loading('Проверка прокси...', 0)
        try {
            const response = await accountsApi.checkProxy(id)
            hide()
            setProxyCheckResult(response.data)
            setProxyCheckModalOpen(true)
        } catch (error: any) {
            hide()
            message.error(error.response?.data?.detail || 'Ошибка проверки')
        }
    }

    const openEditModal = (record: any) => {
        setSelectedAccount(record)
        editForm.setFieldsValue({
            proxy_id: record.proxy_id,
            pool_geo: record.pool_geo || undefined,
            pool_temperature: record.pool_temperature || DEFAULT_TEMPERATURE,
            pool_enabled: record.pool_enabled !== false,
            pool_weight: record.pool_weight || 100,
            daily_send_limit: record.daily_send_limit ?? undefined,
        })
        setEditModalOpen(true)
    }

    const openTdataModal = (record: any) => {
        setSelectedAccount(record)
        setTdataModalOpen(true)
    }

    const openCloudPassword = async () => {
        if (!selectedAccount) return
        cloudPwdForm.resetFields()
        setCloudPwdState(null)
        setCloudPwdOpen(true)
        setCloudPwdStateLoading(true)
        try {
            const { data } = await accountsApi.getCloudPassword(selectedAccount.id)
            setCloudPwdState(data)
        } catch {
            setCloudPwdState(null)
        } finally {
            setCloudPwdStateLoading(false)
        }
    }

    const handleCloudPassword = async (values: any) => {
        if (!selectedAccount) return
        setCloudPwdSaving(true)
        try {
            await accountsApi.setCloudPassword(selectedAccount.id, {
                new_password: values.new_password || '',
                current_password: values.current_password || '',
                hint: values.hint || '',
            })
            message.success(values.new_password ? 'Облачный пароль установлен' : 'Облачный пароль снят')
            setCloudPwdOpen(false)
            cloudPwdForm.resetFields()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка смены пароля')
        } finally {
            setCloudPwdSaving(false)
        }
    }

    const openProfileModal = (record: any) => {
        setSelectedAccount(record)
        setProfileAvatarFile(null)
        setProfileRemoveAvatar(false)
        profileForm.setFieldsValue({
            first_name: record.first_name || '',
            last_name: record.last_name || '',
            username: record.username || '',
            bio: record.bio || '',
        })
        setProfileModalOpen(true)
    }

    const handleUpdateProfile = async (values: any) => {
        if (!selectedAccount) return

        const payload: { first_name?: string; last_name?: string; username?: string; bio?: string; avatar?: File | null; remove_avatar?: boolean } = {}
        if ((values.first_name || '') !== (selectedAccount.first_name || '')) payload.first_name = values.first_name || ''
        if ((values.last_name || '') !== (selectedAccount.last_name || '')) payload.last_name = values.last_name || ''
        if ((values.username || '') !== (selectedAccount.username || '')) payload.username = values.username || ''
        if ((values.bio || '') !== (selectedAccount.bio || '')) payload.bio = values.bio || ''
        if (profileAvatarFile) payload.avatar = profileAvatarFile
        if (profileRemoveAvatar) payload.remove_avatar = true

        if (Object.keys(payload).length === 0) {
            message.info('Нет изменений для сохранения')
            return
        }

        setProfileSaving(true)
        const hideLoading = message.loading('Отправляем изменения в Telegram...', 0)
        try {
            await accountsApi.updateProfile(selectedAccount.id, payload)
            hideLoading()
            message.success('Профиль аккаунта обновлён')
            setProfileModalOpen(false)
            setSelectedAccount(null)
            profileForm.resetFields()
            setProfileAvatarFile(null)
            setProfileRemoveAvatar(false)
            fetchAccounts()
        } catch (error: any) {
            hideLoading()
            message.error(error.response?.data?.detail || 'Ошибка обновления профиля')
        } finally {
            setProfileSaving(false)
        }
    }

    const formatDateTime = (value?: string | null) => (
        value ? new Date(value).toLocaleString('ru-RU') : null
    )

    const formatRemainingTime = (value?: string | null) => {
        if (!value) return null
        const diffMs = new Date(value).getTime() - Date.now()
        if (diffMs <= 0) return 'время уже прошло'
        const minutes = Math.ceil(diffMs / 60000)
        if (minutes < 60) return `${minutes} мин.`
        const hours = Math.floor(minutes / 60)
        const restMinutes = minutes % 60
        return restMinutes ? `${hours} ч. ${restMinutes} мин.` : `${hours} ч.`
    }

    const blockTypeLabels: Record<string, string> = {
        account_banned: 'Аккаунт заблокирован Telegram',
        account_deactivated: 'Аккаунт деактивирован',
        connection_error: 'Ошибка соединения',
        flood_premium_wait: 'Лимит Telegram Premium',
        flood_premium_wait_unknown_until: 'Лимит Telegram Premium без точного срока',
        flood_test_phone_wait: 'Тестовый flood wait',
        flood_test_phone_wait_unknown_until: 'Тестовый flood wait без точного срока',
        flood_wait: 'Flood wait',
        flood_wait_unknown_until: 'Flood wait без точного срока',
        hard_ban: 'Постоянный бан номера',
        limited: 'Ограничение Telegram',
        password_too_fresh: 'Пароль изменён слишком недавно',
        password_too_fresh_unknown_until: 'Пароль изменён слишком недавно, точный срок не передан',
        recipient_or_privacy: 'Ограничение получателя или приватность',
        session_invalid: 'Сессия недействительна',
        session_too_fresh: 'Сессия слишком новая',
        session_too_fresh_unknown_until: 'Сессия слишком новая, точный срок не передан',
        slowmode_wait: 'Slow mode',
        slowmode_wait_unknown_until: 'Slow mode без точного срока',
        takeout_init_delay: 'Задержка Takeout',
        takeout_init_delay_unknown_until: 'Задержка Takeout без точного срока',
        telegram_configuration_error: 'Ошибка настроек Telegram',
        telegram_rate_limit_unknown_until: 'Лимит Telegram без точного срока',
        temporary_limit: 'Временное ограничение без точного срока',
        two_factor_confirm_wait: 'Ожидание 2FA-подтверждения',
        two_factor_confirm_wait_unknown_until: 'Ожидание 2FA-подтверждения без точного срока',
        unknown_error: 'Неизвестная ошибка',
    }

    const getStatusTag = (record: any) => {
        const config: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
            active: { color: record.is_connected ? 'success' : 'warning', icon: record.is_connected ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />, label: record.is_connected ? 'Активен' : 'Нет соединения' },
            inactive: { color: 'default', icon: <CloseCircleOutlined />, label: 'Сессия недоступна' },
            banned: { color: 'error', icon: <CloseCircleOutlined />, label: 'Заблокирован Telegram' },
            limited: { color: 'warning', icon: <ExclamationCircleOutlined />, label: 'Ограничен Telegram' },
            processing: { color: 'processing', icon: <ExclamationCircleOutlined />, label: 'Авторизация' },
        }
        let { color, icon, label } = config[record.status] || { color: 'default', icon: <QuestionCircleOutlined />, label: record.status || 'Неизвестно' }
        const blockedUntil = record.blocked_until ? new Date(record.blocked_until) : null
        const hasActiveBlock = record.health_status === 'temporarily_blocked' && (!blockedUntil || blockedUntil.getTime() > Date.now())
        if (record.health_status === 'banned_by_telegram') {
            color = 'error'
            icon = <CloseCircleOutlined />
            label = 'Заблокирован Telegram'
        } else if (record.health_status === 'hard_banned') {
            color = 'error'
            icon = <CloseCircleOutlined />
            label = 'Хард бан'
        } else if (record.health_status === 'session_invalid') {
            color = 'error'
            icon = <CloseCircleOutlined />
            label = 'Сессия недействительна'
        } else if (record.health_status === 'configuration_error') {
            color = 'error'
            icon = <ExclamationCircleOutlined />
            label = 'Ошибка настроек'
        } else if (record.health_status === 'connection_error') {
            color = 'warning'
            icon = <ExclamationCircleOutlined />
            label = 'Нет соединения'
        } else if (hasActiveBlock) {
            color = 'warning'
            icon = <ExclamationCircleOutlined />
            label = 'Временно ограничен'
        } else if (record.health_status === 'limited') {
            color = 'warning'
            icon = <ExclamationCircleOutlined />
            label = 'Ограничен Telegram'
        } else if (record.health_status === 'recipient_restricted') {
            color = 'warning'
            icon = <ExclamationCircleOutlined />
            label = 'Ограничение получателя'
        } else if (record.health_status === 'error' && record.last_error) {
            color = 'error'
            icon = <ExclamationCircleOutlined />
            label = 'Ошибка отправки'
        }
        const checkedAt = formatDateTime(record.status_checked_at) || 'ещё не проверялся автоматически'
        const healthCheckedAt = formatDateTime(record.health_checked_at)
        const blockedUntilText = formatDateTime(record.blocked_until)
        const remainingTime = formatRemainingTime(record.blocked_until)
        const healthReason = record.block_reason || record.last_error
        const blockTypeText = record.block_type ? (blockTypeLabels[record.block_type] || record.block_type) : null
        const tooltip = (
            <div style={{ maxWidth: 360 }}>
                <div>{record.status_reason || 'Причина статуса пока не зафиксирована.'}</div>
                {healthReason && <div style={{ marginTop: 6 }}>Отправка: {healthReason}</div>}
                {blockTypeText && <div style={{ marginTop: 6, opacity: 0.75 }}>Тип ограничения: {blockTypeText}</div>}
                {blockedUntilText && (
                    <div style={{ marginTop: 6, opacity: 0.75 }}>
                        До разблокировки: {blockedUntilText}{remainingTime ? ` (${remainingTime})` : ''}
                    </div>
                )}
                {record.health_status === 'temporarily_blocked' && !blockedUntilText && (
                    <div style={{ marginTop: 6, opacity: 0.75 }}>
                        Точное время разблокировки Telegram не передал.
                    </div>
                )}
                {healthCheckedAt && <div style={{ marginTop: 6, opacity: 0.75 }}>Проверка отправки: {healthCheckedAt}</div>}
                <div style={{ marginTop: 6, opacity: 0.75 }}>Последняя проверка: {checkedAt}</div>
            </div>
        )
        return (
            <Space size={4}>
                <Tag color={color} icon={icon}>{label}</Tag>
                <Tooltip title={tooltip} placement="topRight">
                    <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                </Tooltip>
            </Space>
        )
    }


    // Аккаунт - общий инструмент, а не чья-то собственность: управляют им
    // тимлид и суперадмин, которым раздел и доступен.
    const canManageAccount = (_record: any) => isTeamLead

    // Сортировка по клику на заголовок: первый клик - по возрастанию, второй -
    // по убыванию, третий - снова постоянный порядок по умолчанию.
    const sortProps = (key: string, compare: (a: any, b: any) => number) => ({
        sorter: compare,
        sortOrder: accountTableView.sortKey === key ? accountTableView.sortOrder : null,
        sortDirections: ['ascend' as const, 'descend' as const],
    })

    const columns = [
        {
            title: 'Телефон',
            dataIndex: 'phone',
            key: 'phone',
            ...sortProps('phone', (a, b) => naturalCompare(a.phone, b.phone)),
        },
        {
            title: 'Юзернейм',
            dataIndex: 'username',
            key: 'username',
            render: (text: string) => text ? `@${text}` : '-',
            ...sortProps('username', (a, b) => naturalCompare(a.username, b.username)),
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (_status: string, record: any) => getStatusTag(record),
            // Проблемные - сверху: заблокированные, потом ограниченные, потом без связи.
            ...sortProps('status', (a, b) => accountStatusRank(a) - accountStatusRank(b) || compareAccountsDefault(a, b)),
        },
        {
            title: 'Подключен',
            dataIndex: 'is_connected',
            key: 'is_connected',
            ...sortProps('is_connected', (a, b) => Number(Boolean(a.is_connected)) - Number(Boolean(b.is_connected)) || compareAccountsDefault(a, b)),
            render: (v: boolean) => (
                <Tag color={v ? 'success' : 'default'}>{v ? 'Да' : 'Нет'}</Tag>
            ),
        },
        {
            title: 'Гео пул',
            key: 'pool_geo',
            ...sortProps('pool_geo', (a, b) => naturalCompare(a.pool_geo, b.pool_geo) || compareAccountsDefault(a, b)),
            render: (_: any, record: any) => record.pool_geo ? (
                <Tag color={record.pool_enabled === false ? 'default' : 'blue'}>{record.pool_geo}</Tag>
            ) : '-',
        },
        {
            title: 'Температура',
            key: 'pool_temperature',
            width: 130,
            ...sortProps('pool_temperature', (a, b) => temperatureRank(a.pool_temperature) - temperatureRank(b.pool_temperature) || compareAccountsDefault(a, b)),
            render: (_: any, record: any) => record.pool_temperature ? (
                <Tag color={temperatureColor(record.pool_temperature, 'default')}>{temperatureLabel(record.pool_temperature)}</Tag>
            ) : '-',
        },
        {
            title: 'Прокси',
            key: 'proxy',
            ...sortProps('proxy', (a, b) => naturalCompare(a.proxy?.name, b.proxy?.name) || compareAccountsDefault(a, b)),
            render: (_: any, record: any) =>
                record.proxy?.name || '-',
        },
        {
            title: 'Внешний IP',
            key: 'exit_ip',
            width: 230,
            ...sortProps('exit_ip', (a, b) => naturalCompare(a.exit_ip, b.exit_ip) || compareAccountsDefault(a, b)),
            render: (_: any, record: any) => {
                const country = countryName(record.exit_country_code)
                return (
                    <Space direction="vertical" size={2}>
                        {record.exit_ip ? (
                            <span style={{ fontWeight: 600 }}>{record.exit_ip}</span>
                        ) : (
                            <span style={{ color: 'var(--text-muted)' }}>не проверен</span>
                        )}
                        {record.exit_ip && (
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                {[country, record.exit_city].filter(Boolean).join(', ') || 'страна не определена'}
                                {' · '}
                                {record.exit_via_proxy ? 'через прокси' : 'напрямую с сервера'}
                            </span>
                        )}
                        {record.exit_error && (
                            <Tooltip title={record.exit_error}>
                                <span style={{ fontSize: 12, color: 'var(--error)' }}>последняя проверка не удалась</span>
                            </Tooltip>
                        )}
                        <Space size={6}>
                            {record.exit_checked_at && (
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                    {new Date(record.exit_checked_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </span>
                            )}
                            <Button
                                size="small"
                                type="link"
                                style={{ padding: 0, height: 'auto' }}
                                loading={exitChecking.has(record.id)}
                                onClick={() => checkExitIp(record.id)}
                            >
                                Проверить
                            </Button>
                        </Space>
                    </Space>
                )
            },
        },
        {
            title: 'Чатов',
            dataIndex: 'chats_count',
            key: 'chats_count',
            ...sortProps('chats_count', (a, b) => (a.chats_count || 0) - (b.chats_count || 0) || compareAccountsDefault(a, b)),
            render: (count: number, record: any) => (
                <Button type="link" onClick={() => openChatsModal(record)} style={{ padding: 0 }}>
                    {count || 0}
                </Button>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space size={4}>
                    {canManageAccount(record) && (
                        <>
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => openEditModal(record)}
                                title="Редактировать статус/прокси"
                                style={{
                                    color: '#6366f1',
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    borderRadius: 8,
                                }}
                            />
                            <Button
                                type="text"
                                icon={<CloudServerOutlined />}
                                onClick={() => openTdataModal(record)}
                                title="Загрузить TDATA/metadata"
                                style={{
                                    color: '#8b5cf6',
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    borderRadius: 8,
                                }}
                            />
                            <Button
                                type="text"
                                icon={<IdcardOutlined />}
                                onClick={() => openProfileModal(record)}
                                title="Редактировать профиль Telegram (имя, тег, био, аватар)"
                                style={{
                                    color: '#14b8a6',
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    borderRadius: 8,
                                }}
                            />
                        </>
                    )}
                    {record.is_connected ? (
                        <Popconfirm
                            title="Отключить аккаунт?"
                            onConfirm={() => handleDisconnect(record.id)}
                            disabled={!canManageAccount(record)}
                        >
                            <Button
                                type="text"
                                icon={<CloseCircleOutlined />}
                                title="Отключить"
                                disabled={!canManageAccount(record)}
                                style={{
                                    color: canManageAccount(record) ? '#f59e0b' : '#d9d9d9',
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    borderRadius: 8,
                                }}
                            />
                        </Popconfirm>
                    ) : (
                        <Button
                            type="text"
                            icon={<CheckCircleOutlined />}
                            onClick={() => handleConnect(record.id)}
                            title="Подключить"
                            disabled={!canManageAccount(record)}
                            style={{
                                color: canManageAccount(record) ? '#10b981' : '#d9d9d9',
                                width: 32,
                                height: 32,
                                padding: 0,
                                borderRadius: 8,
                            }}
                        />
                    )}
                    <Button
                        type="text"
                        icon={<DesktopOutlined />}
                        onClick={() => openSessionsModal(record)}
                        title="Telegram-сессии"
                        disabled={!canManageAccount(record)}
                        style={{
                            color: canManageAccount(record) ? '#22c55e' : '#d9d9d9',
                            width: 32,
                            height: 32,
                            padding: 0,
                            borderRadius: 8,
                        }}
                    />
                    <Button
                        type="text"
                        icon={<GlobalOutlined />}
                        onClick={() => handleCheckProxy(record.id)}
                        title="Проверить IP прокси"
                        style={{
                            color: '#1890ff',
                            width: 32,
                            height: 32,
                            padding: 0,
                            borderRadius: 8,
                        }}
                    />
                    {isTeamLead && (
                        <Popconfirm
                            title="Удалить аккаунт?"
                            onConfirm={() => handleDelete(record.id)}
                        >
                            <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                title="Удалить"
                                style={{
                                    color: '#ef4444',
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    borderRadius: 8,
                                }}
                            />
                        </Popconfirm>
                    )}
                </Space >
            ),
        },
    ]

    if (accountsTab === 'stats') {
        return (
            <div style={{ padding: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <h1 style={{ color: 'white', margin: 0 }}>TG Аккаунты</h1>
                    <Segmented
                        value={accountsTab}
                        onChange={(value) => setAccountsTab(value as 'list' | 'stats')}
                        options={[
                            { value: 'list', label: 'Аккаунты' },
                            { value: 'stats', label: 'Статистика' },
                        ]}
                    />
                </div>
                <AccountsStatistics />
            </div>
        )
    }

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h1 style={{ color: 'white', margin: 0 }}>TG Аккаунты</h1>
                <Space>
                    <Segmented
                        value={accountsTab}
                        onChange={(value) => setAccountsTab(value as 'list' | 'stats')}
                        options={[
                            { value: 'list', label: 'Аккаунты' },
                            { value: 'stats', label: 'Статистика' },
                        ]}
                    />
                    {isTeamLead && (
                        <Button
                            icon={<ReloadOutlined spin={refreshingStatuses} />}
                            onClick={handleRefreshStatuses}
                            loading={refreshingStatuses}
                        >
                            Актуализировать статусы
                        </Button>
                    )}
                    <Button
                        icon={<CloudServerOutlined />}
                        onClick={() => setNewTdataModalOpen(true)}
                    >
                        Подключить через TDATA
                    </Button>
                    <Button
                        icon={<CloudServerOutlined />}
                        onClick={() => setBulkTdataModalOpen(true)}
                    >
                        Массово TDATA
                    </Button>
                    <Button
                        icon={<MobileOutlined />}
                        onClick={() => setPhoneAuthModalOpen(true)}
                    >
                        Авторизация по номеру
                    </Button>
                    <Button
                        icon={<PlusOutlined />}
                        onClick={() => setAuthModalOpen(true)}
                    >
                        QR Авторизация
                    </Button>
                </Space>
            </div>

            <div className="account-geo-pools">
                <button
                    type="button"
                    className={!selectedPoolGeo ? 'account-geo-pool is-active' : 'account-geo-pool'}
                    onClick={() => setSelectedPoolGeo(null)}
                >
                    <strong>Все гео</strong>
                    <span>{accounts.length} аккаунтов</span>
                </button>
                {accountPoolStats.map((item) => (
                    <button
                        type="button"
                        key={item.geo}
                        className={selectedPoolGeo === item.geo ? 'account-geo-pool is-active' : 'account-geo-pool'}
                        onClick={() => setSelectedPoolGeo((current) => current === item.geo ? null : item.geo)}
                    >
                        <strong>{item.geo}</strong>
                        <span>{item.total} всего · {item.connected} подключено</span>
                    </button>
                ))}
            </div>

            <div className="account-geo-pools">
                <button
                    type="button"
                    className={!selectedPoolTemperature ? 'account-geo-pool is-active' : 'account-geo-pool'}
                    onClick={() => setSelectedPoolTemperature(null)}
                >
                    <strong>Все температуры</strong>
                    <span>{accounts.length} аккаунтов</span>
                </button>
                {accountTemperatureStats.map((item) => (
                    <button
                        type="button"
                        key={item.value}
                        className={selectedPoolTemperature === item.value ? 'account-geo-pool is-active' : 'account-geo-pool'}
                        onClick={() => setSelectedPoolTemperature((current) => current === item.value ? null : item.value)}
                    >
                        <strong style={{ color: temperatureColor(item.value, 'inherit') }}>{item.label}</strong>
                        <span>{item.total} всего · {item.connected} подключено</span>
                    </button>
                ))}
            </div>

            <Space style={{ marginBottom: 12 }} wrap>
                <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder="Поиск по юзернейму или телефону"
                    value={accountSearch}
                    onChange={(event) => setAccountSearch(event.target.value)}
                    style={{ width: 320 }}
                />
                {accountTableView.sortKey && (
                    <Button onClick={() => setAccountTableView((view) => ({ ...view, sortKey: null, sortOrder: null }))}>
                        Сбросить сортировку
                    </Button>
                )}
                <Tooltip title="Проверить, с какого адреса каждый аккаунт выходит в интернет — через свой прокси или напрямую. Аккаунты проверяются по очереди.">
                    <Button
                        icon={<GlobalOutlined />}
                        loading={Boolean(exitCheckAll)}
                        onClick={() => checkExitIpAll(filteredAccounts.map((item) => item.id))}
                    >
                        {exitCheckAll ? `Проверка IP ${exitCheckAll.done}/${exitCheckAll.total}` : 'Проверить все IP'}
                    </Button>
                </Tooltip>
            </Space>

            <Table
                dataSource={filteredAccounts}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{
                    pageSize: accountTableView.pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total) => `${total} аккаунтов`,
                }}
                onChange={(pagination, _filters, sorter) => {
                    const single = Array.isArray(sorter) ? sorter[0] : sorter
                    setAccountTableView((view) => ({
                        pageSize: pagination.pageSize || view.pageSize,
                        sortKey: single?.order ? String(single.columnKey || '') || null : null,
                        sortOrder: single?.order || null,
                    }))
                }}
            />


            <Modal
                title="Новый TG аккаунт"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
            >
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item
                        name="phone"
                        label="Номер телефона"
                        rules={[{ required: true, message: 'Введите номер телефона' }]}
                    >
                        <Input placeholder="+79001234567" />
                    </Form.Item>
                    <Form.Item name="proxy_id" label="Прокси">
                        <Select placeholder="Выберите прокси" allowClear>
                            {proxies.map((p: any) => (
                                <Select.Option key={p.id} value={p.id}>
                                    {p.name} ({p.host}:{p.port})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="pool_geo" label="Гео пул">
                        <Select
                            placeholder="Выберите гео пула"
                            allowClear
                            showSearch
                            options={GEO_OPTIONS.map((geo) => ({ value: geo, label: geo }))}
                        />
                    </Form.Item>
                    <Form.Item name="pool_temperature" label="Температура" initialValue={DEFAULT_TEMPERATURE}>
                        <Select options={TEMPERATURE_OPTIONS} />
                    </Form.Item>
                </Form>
            </Modal>


            <Modal
                title={`Редактирование: ${selectedAccount?.phone || ''}`}
                open={editModalOpen}
                onCancel={() => {
                    setEditModalOpen(false)
                    setSelectedAccount(null)
                }}
                onOk={() => editForm.submit()}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Form.Item name="proxy_id" label="Прокси">
                        <Select placeholder="Выберите прокси" allowClear>
                            {proxies.map((p: any) => (
                                <Select.Option key={p.id} value={p.id}>
                                    {p.name} ({p.host}:{p.port})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="pool_geo" label="Гео пул">
                        <Select
                            placeholder="Выберите гео пула"
                            allowClear
                            showSearch
                            options={GEO_OPTIONS.map((geo) => ({ value: geo, label: geo }))}
                        />
                    </Form.Item>
                    <Form.Item name="pool_temperature" label="Температура">
                        <Select options={TEMPERATURE_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="pool_enabled" label="Использовать в пуле" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item name="pool_weight" label="Вес в пуле">
                        <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="daily_send_limit" label="Лимит первых сообщений в сутки" tooltip="Пусто = общий лимит из настроек рассылки. Разным аккаунтам (холодным/прогретым) можно задавать разный лимит.">
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="Общий лимит из настроек" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Профиль Telegram: ${selectedAccount?.phone || ''}`}
                open={profileModalOpen}
                closable={!profileSaving}
                maskClosable={!profileSaving}
                cancelButtonProps={{ disabled: profileSaving }}
                onCancel={() => {
                    if (profileSaving) return
                    setProfileModalOpen(false)
                    setSelectedAccount(null)
                    profileForm.resetFields()
                    setProfileAvatarFile(null)
                    setProfileRemoveAvatar(false)
                }}
                onOk={() => profileForm.submit()}
                confirmLoading={profileSaving}
            >
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Не меняйте данные аккаунта слишком часто"
                    description="Смена имени/тега/фото/био — обращение к живому Telegram-аккаунту. Частые правки одного и того же аккаунта повышают риск флага/потери авторизации. После смены данных рекомендуется дать аккаунту отдохнуть некоторое время перед активным использованием."
                />
                <Space direction="vertical" align="center" style={{ width: '100%', marginBottom: 16 }}>
                    <Avatar
                        size={72}
                        src={profileAvatarFile ? URL.createObjectURL(profileAvatarFile) : (!profileRemoveAvatar ? selectedAccount?.avatar_url : undefined)}
                        icon={<UserOutlined />}
                    />
                    <Space>
                        <Upload
                            accept="image/*"
                            maxCount={1}
                            showUploadList={false}
                            beforeUpload={(file) => {
                                if (file.size > 10 * 1024 * 1024) {
                                    message.error('Файл слишком большой (макс. 10 МБ)')
                                    return Upload.LIST_IGNORE
                                }
                                setProfileAvatarFile(file)
                                setProfileRemoveAvatar(false)
                                return false
                            }}
                        >
                            <Button icon={<UploadOutlined />} size="small">Загрузить фото</Button>
                        </Upload>
                        {(selectedAccount?.avatar_url || profileAvatarFile) && (
                            <Button
                                size="small"
                                danger
                                onClick={() => {
                                    setProfileAvatarFile(null)
                                    setProfileRemoveAvatar(true)
                                }}
                            >
                                Удалить фото
                            </Button>
                        )}
                    </Space>
                </Space>
                <Form form={profileForm} onFinish={handleUpdateProfile} layout="vertical">
                    <Form.Item name="first_name" label="Имя">
                        <Input maxLength={64} placeholder="Имя" />
                    </Form.Item>
                    <Form.Item name="last_name" label="Фамилия">
                        <Input maxLength={64} placeholder="Фамилия" />
                    </Form.Item>
                    <Form.Item name="username" label="Тег (username)" tooltip="Без @. Если тег занят другим аккаунтом, Telegram вернёт ошибку.">
                        <Input maxLength={32} placeholder="username" />
                    </Form.Item>
                    <Form.Item name="bio" label="Био">
                        <Input.TextArea maxLength={70} showCount rows={3} placeholder="О себе" />
                    </Form.Item>
                </Form>
                <div style={{ borderTop: '1px solid var(--lead-border, #2a2a3a)', marginTop: 8, paddingTop: 12 }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: 13 }}>
                            Облачный пароль (2FA)
                        </span>
                        <Button icon={<SafetyCertificateOutlined />} onClick={openCloudPassword}>
                            Сменить облачный пароль
                        </Button>
                    </Space>
                </div>
            </Modal>

            <Modal
                title={`Облачный пароль: ${selectedAccount?.phone || ''}`}
                open={cloudPwdOpen}
                closable={!cloudPwdSaving}
                maskClosable={!cloudPwdSaving}
                cancelButtonProps={{ disabled: cloudPwdSaving }}
                onCancel={() => { if (!cloudPwdSaving) { setCloudPwdOpen(false); cloudPwdForm.resetFields() } }}
                onOk={() => cloudPwdForm.submit()}
                okText="Сохранить"
                confirmLoading={cloudPwdSaving}
            >
                {cloudPwdStateLoading ? (
                    <Spin />
                ) : (
                    <>
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={cloudPwdState?.has_password ? 'На аккаунте уже стоит облачный пароль' : 'Облачный пароль на аккаунте не установлен'}
                            description={
                                <Space direction="vertical" size={2}>
                                    {cloudPwdState?.has_password && cloudPwdState?.hint && <span>Подсказка: {cloudPwdState.hint}</span>}
                                    {cloudPwdState?.email_unconfirmed_pattern && <span>Неподтверждённая почта: {cloudPwdState.email_unconfirmed_pattern}</span>}
                                    {cloudPwdState?.pending_reset_date && <span style={{ color: '#faad14' }}>Запущен сброс пароля: {cloudPwdState.pending_reset_date}</span>}
                                    <span>Смена идёт через текущую сессию аккаунта, без входа по SMS.</span>
                                </Space>
                            }
                        />
                        <Form form={cloudPwdForm} onFinish={handleCloudPassword} layout="vertical">
                            {cloudPwdState?.has_password && (
                                <Form.Item name="current_password" label="Текущий пароль" rules={[{ required: true, message: 'Нужен текущий пароль' }]}>
                                    <Input.Password placeholder="Текущий облачный пароль" />
                                </Form.Item>
                            )}
                            <Form.Item name="new_password" label="Новый пароль" tooltip="Оставьте пустым, чтобы снять пароль">
                                <Input.Password placeholder="Новый облачный пароль" />
                            </Form.Item>
                            <Form.Item name="hint" label="Подсказка (необязательно)">
                                <Input maxLength={100} placeholder="Подсказка к паролю" />
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Modal>


            <Modal
                title={`Загрузка TDATA/metadata: ${selectedAccount?.phone || ''}`}
                open={tdataModalOpen}
                onCancel={() => {
                    setTdataModalOpen(false)
                    setSelectedAccount(null)
                    resetTdataUploadState()
                }}
                footer={null}
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <p style={{ marginBottom: 16, color: '#888' }}>
                        Загрузите ZIP архив с TDATA, session или metadata
                    </p>
                    <Upload.Dragger
                        accept=".zip"
                        maxCount={1}
                        disabled={uploading}
                        beforeUpload={(file) => {
                            if (!isZipFile(file)) return Upload.LIST_IGNORE
                            setTdataFile(file)
                            return false
                        }}
                        onRemove={() => {
                            setTdataFile(null)
                            return true
                        }}
                    >
                        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                        <p className="ant-upload-text">Перетащите ZIP архив сюда</p>
                        <p className="ant-upload-hint">{tdataFile?.name || 'TDATA / metadata архив'}</p>
                    </Upload.Dragger>
                    <Input.Password
                        style={{ marginTop: 16 }}
                        placeholder="Пароль ZIP архива, если есть"
                        value={tdataArchivePassword}
                        onChange={(event) => setTdataArchivePassword(event.target.value)}
                        disabled={uploading}
                    />
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        block
                        loading={uploading}
                        disabled={!tdataFile}
                        onClick={handleImportTdata}
                        style={{ marginTop: 16 }}
                    >
                        Загрузить
                    </Button>
                </div>
            </Modal>


            <Modal
                title="Подключить через TDATA"
                open={newTdataModalOpen}
                onCancel={() => {
                    setNewTdataModalOpen(false)
                    resetTdataUploadState()
                }}
                footer={null}
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <p style={{ marginBottom: 16, color: '#888' }}>
                        Загрузите ZIP архив с TDATA, session или metadata.<br />
                        Номер телефона и данные аккаунта будут извлечены автоматически.
                    </p>
                    <Select
                        allowClear
                        placeholder="Гео пула"
                        style={{ width: '100%', marginBottom: 16 }}
                        value={bulkTdataGeo}
                        onChange={setBulkTdataGeo}
                        options={GEO_OPTIONS.map((geo) => ({ value: geo, label: geo }))}
                    />
                    <Upload.Dragger
                        accept=".zip"
                        maxCount={1}
                        disabled={uploading}
                        beforeUpload={(file) => {
                            if (!isZipFile(file)) return Upload.LIST_IGNORE
                            setNewTdataFile(file)
                            return false
                        }}
                        onRemove={() => {
                            setNewTdataFile(null)
                            return true
                        }}
                    >
                        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                        <p className="ant-upload-text">Перетащите ZIP архив сюда</p>
                        <p className="ant-upload-hint">{newTdataFile?.name || 'TDATA / metadata архив'}</p>
                    </Upload.Dragger>
                    <Input.Password
                        style={{ marginTop: 16 }}
                        placeholder="Пароль ZIP архива, если есть"
                        value={newTdataArchivePassword}
                        onChange={(event) => setNewTdataArchivePassword(event.target.value)}
                        disabled={uploading}
                    />
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        block
                        loading={uploading}
                        disabled={!newTdataFile}
                        onClick={handleCreateFromTdata}
                        style={{ marginTop: 16 }}
                    >
                        Подключить через TDATA
                    </Button>
                </div>
            </Modal>


            <Modal
                title="Массовое подключение TDATA в пул"
                open={bulkTdataModalOpen}
                onCancel={() => {
                    setBulkTdataModalOpen(false)
                    resetTdataUploadState()
                }}
                footer={null}
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <p style={{ marginBottom: 16, color: '#888' }}>
                        Выберите несколько ZIP архивов TDATA/metadata. Для выбранного гео каждому аккаунту будет назначен один свободный прокси того же гео.
                    </p>
                    <Select
                        allowClear
                        placeholder="Гео пула и прокси"
                        style={{ width: '100%', marginBottom: 16 }}
                        value={bulkTdataGeo}
                        onChange={setBulkTdataGeo}
                        options={GEO_OPTIONS.map((geo) => ({ value: geo, label: geo }))}
                    />
                    <Upload.Dragger
                        accept=".zip"
                        multiple
                        disabled={uploading}
                        beforeUpload={(file) => {
                            if (!isZipFile(file)) return Upload.LIST_IGNORE
                            setBulkTdataFiles((items) => [...items.filter((item) => item.name !== file.name || item.size !== file.size), file])
                            return false
                        }}
                        onRemove={(file) => {
                            setBulkTdataFiles((items) => items.filter((item) => item.name !== file.name || item.size !== file.size))
                            return true
                        }}
                    >
                        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                        <p className="ant-upload-text">Перетащите ZIP архивы TDATA сюда</p>
                        <p className="ant-upload-hint">Выбрано: {bulkTdataFiles.length}</p>
                    </Upload.Dragger>
                    <Input.Password
                        style={{ marginTop: 16 }}
                        placeholder="Общий пароль ZIP архивов, если есть"
                        value={bulkTdataArchivePassword}
                        onChange={(event) => setBulkTdataArchivePassword(event.target.value)}
                        disabled={uploading}
                    />
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        block
                        loading={uploading}
                        disabled={!bulkTdataFiles.length}
                        onClick={handleBulkCreateFromTdata}
                        style={{ marginTop: 16 }}
                    >
                        Подключить пачку
                    </Button>
                </div>
            </Modal>


            <Modal
                title="Авторизация через QR код"
                open={authModalOpen}
                onCancel={handleAuthCancel}
                footer={null}
                width={480}
            >
                {authStep === 'qr' && (
                    <div style={{ padding: '20px 0' }}>
                        {authLoading && !qrUrl ? (
                            <div style={{ padding: 40 }}>
                                <div style={{ fontSize: 16, color: '#888', textAlign: 'center' }}>Генерация QR кода...</div>
                            </div>
                        ) : qrUrl ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{
                                    background: 'white',
                                    borderRadius: 16,
                                    padding: 16,
                                    display: 'inline-block',
                                    marginBottom: 20
                                }}>
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(qrUrl)}`}
                                        alt="QR Code"
                                        style={{ width: 280, height: 280, display: 'block' }}
                                    />
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <p style={{ color: '#10b981', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                                        Отсканируйте QR код в Telegram
                                    </p>
                                    <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>
                                        Откройте Telegram → Настройки → Устройства → Подключить устройство
                                    </p>
                                </div>
                                <div style={{
                                    background: 'rgba(16, 185, 129, 0.1)',
                                    border: '1px solid rgba(16, 185, 129, 0.2)',
                                    borderRadius: 8,
                                    padding: '8px 16px',
                                    display: 'inline-block'
                                }}>
                                    <span style={{ color: '#10b981', fontSize: 13 }}>⏳ Ожидание сканирования...</span>
                                </div>
                            </div>
                        ) : (
                            <Form
                                form={qrAuthForm}
                                layout="vertical"
                                onFinish={handleStartQrAuth}
                            >
                                <p style={{ color: '#888', marginBottom: 24, textAlign: 'center' }}>
                                    После авторизации аккаунт будет добавлен в выбранный гео-пул.
                                </p>
                                <Form.Item
                                    name="pool_geo"
                                    label="Гео-пул"
                                    rules={[{ required: true, message: 'Выберите гео-пул' }]}
                                >
                                    <Select
                                        placeholder="Выберите гео-пул"
                                        options={GEO_OPTIONS.map((geo) => ({ value: geo, label: geo }))}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="pool_temperature"
                                    label="Температура"
                                    initialValue={DEFAULT_TEMPERATURE}
                                    rules={[{ required: true, message: 'Выберите температуру' }]}
                                >
                                    <Select options={TEMPERATURE_OPTIONS} />
                                </Form.Item>
                                <Form.Item name="proxy_id" label="Прокси (необязательно)">
                                    <Select placeholder="Выберите прокси" allowClear>
                                        {proxies.map((proxy: any) => (
                                            <Select.Option key={proxy.id} value={proxy.id}>
                                                {proxy.name} ({proxy.host}:{proxy.port})
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Button type="primary" htmlType="submit" loading={authLoading} block>
                                    Создать QR-код
                                </Button>
                            </Form>
                        )}
                    </div>
                )}

                {authStep === '2fa' && (
                    <Form
                        layout="vertical"
                        onFinish={(values) => handleVerify2fa(values.password)}
                    >
                        <p style={{ marginBottom: 16, color: '#888', textAlign: 'center' }}>
                            QR код отсканирован!<br />
                            На этом аккаунте включена двухфакторная аутентификация.<br />
                            Введите пароль 2FA:
                        </p>
                        <Form.Item
                            name="password"
                            label="Пароль 2FA"
                            rules={[{ required: true, message: 'Введите пароль 2FA' }]}
                        >
                            <Input.Password placeholder="Пароль" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={authLoading} block>
                            Авторизоваться
                        </Button>
                    </Form>
                )}
            </Modal>


            <Modal
                title={phoneAuthStep === 'phone_input' ? 'Авторизация по номеру' : `Авторизация: ${phoneAuthPhone}`}
                open={phoneAuthModalOpen}
                onCancel={handlePhoneAuthCancel}
                footer={null}
                width={480}
            >
                {phoneAuthStep === 'phone_input' && (
                    <Form form={phoneAuthForm} layout="vertical" onFinish={handleCreateAndSendCode}>
                        <p style={{ color: '#888', marginBottom: 24, textAlign: 'center' }}>
                            Введите номер телефона. После авторизации аккаунт будет добавлен в выбранный гео-пул.
                        </p>
                        <Form.Item
                            name="phone"
                            label="Номер телефона"
                            rules={[{ required: true, message: 'Введите номер телефона' }]}
                        >
                            <Input placeholder="+79001234567" />
                        </Form.Item>
                        <Form.Item
                            name="pool_geo"
                            label="Гео-пул"
                            rules={[{ required: true, message: 'Выберите гео-пул' }]}
                        >
                            <Select
                                placeholder="Выберите гео-пул"
                                options={GEO_OPTIONS.map((geo) => ({ value: geo, label: geo }))}
                            />
                        </Form.Item>
                        <Form.Item
                            name="pool_temperature"
                            label="Температура"
                            initialValue={DEFAULT_TEMPERATURE}
                            rules={[{ required: true, message: 'Выберите температуру' }]}
                        >
                            <Select options={TEMPERATURE_OPTIONS} />
                        </Form.Item>
                        <Form.Item name="proxy_id" label="Прокси (необязательно)">
                            <Select placeholder="Выберите прокси" allowClear>
                                {proxies.map((p: any) => (
                                    <Select.Option key={p.id} value={p.id}>
                                        {p.name} ({p.host}:{p.port})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={authLoading}
                            block
                        >
                            Создать и отправить код
                        </Button>
                    </Form>
                )}

                {phoneAuthStep === 'verify_code' && (
                    <Form
                        layout="vertical"
                        onFinish={handleVerifyPhoneCode}
                    >
                        <p style={{ marginBottom: 16, color: '#888', textAlign: 'center' }}>
                            Код отправлен на номер <b>{phoneAuthPhone}</b>.<br />
                            Проверьте системные сообщения в приложении Telegram.
                        </p>
                        <Form.Item
                            name="code"
                            label="Код подтверждения"
                            rules={[{ required: true, message: 'Введите код' }]}
                        >
                            <Input placeholder="12345" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={authLoading} block style={{ marginBottom: 12 }}>
                            Подтвердить код
                        </Button>
                        <Button type="default" onClick={handleResendPhoneCode} loading={authLoading} block>
                            Отправить код по SMS (Переотправить)
                        </Button>
                    </Form>
                )}

                {phoneAuthStep === '2fa' && (
                    <Form
                        layout="vertical"
                        onFinish={handleVerifyPhone2fa}
                    >
                        <p style={{ marginBottom: 16, color: '#888', textAlign: 'center' }}>
                            На этом аккаунте включена двухфакторная аутентификация.<br />
                            Введите пароль 2FA:
                        </p>
                        <Form.Item
                            name="password"
                            label="Пароль 2FA"
                            rules={[{ required: true, message: 'Введите пароль' }]}
                        >
                            <Input.Password placeholder="Пароль" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={authLoading} block>
                            Авторизоваться
                        </Button>
                    </Form>
                )}
            </Modal>


            <Modal
                title={proxyCheckResult?.success ? 'Прокси работает' : 'Ошибка прокси'}
                open={proxyCheckModalOpen}
                onCancel={() => setProxyCheckModalOpen(false)}
                footer={[
                    <Button key="ok" type="primary" onClick={() => setProxyCheckModalOpen(false)}>
                        OK
                    </Button>
                ]}
            >
                {proxyCheckResult && (
                    <div style={{ color: 'white', marginTop: 16 }}>
                        {proxyCheckResult.success ? (
                            <>
                                <p style={{ marginBottom: 8, fontSize: 16 }}>Внешний IP: <b style={{ color: '#52c41a' }}>{proxyCheckResult.ip}</b></p>
                                <p style={{ color: '#94a3b8' }}>Прокси: {proxyCheckResult.proxy}</p>
                            </>
                        ) : (
                            <p style={{ color: '#ff4d4f', fontSize: 16 }}>{proxyCheckResult.message}</p>
                        )}
                    </div>
                )}
            </Modal>


            <Modal
                title={`Telegram-сессии: ${sessionsAccount?.phone || ''}`}
                open={sessionsModalOpen}
                onCancel={closeSessionsModal}
                width={1120}
                footer={[
                    <Button key="refresh" icon={<ReloadOutlined />} onClick={() => fetchAccountSessions()} loading={sessionsLoading}>
                        Обновить
                    </Button>,
                    <Popconfirm
                        key="revoke-others"
                        title="Завершить все другие Telegram-сессии?"
                        onConfirm={handleRevokeOtherSessions}
                        disabled={!accountSessions.some((item) => !item.current) || sessionsActionLoading}
                    >
                        <Button danger disabled={!accountSessions.some((item) => !item.current) || sessionsActionLoading} loading={sessionsActionLoading}>
                            Завершить все другие
                        </Button>
                    </Popconfirm>,
                    <Button key="close" onClick={closeSessionsModal}>Закрыть</Button>,
                ]}
            >
                <Table
                    dataSource={accountSessions}
                    rowKey="hash"
                    loading={sessionsLoading}
                    pagination={false}
                    tableLayout="fixed"
                    scroll={{ x: 1040 }}
                    columns={[
                        {
                            title: 'Устройство',
                            key: 'device',
                            width: 230,
                            render: (_: any, record: any) => (
                                <Space direction="vertical" size={2}>
                                    <Space size={6}>
                                        <span style={{ color: 'white', fontWeight: 600 }}>{record.device_model || '-'}</span>
                                        {record.current && <Tag color="success">Текущая</Tag>}
                                        {record.official_app && <Tag color="blue">Official</Tag>}
                                    </Space>
                                    <span style={{ color: '#94a3b8', fontSize: 12 }}>
                                        {record.app_name || '-'} {record.app_version || ''}
                                    </span>
                                </Space>
                            ),
                        },
                        {
                            title: 'Система',
                            key: 'system',
                            width: 210,
                            render: (_: any, record: any) => (
                                <span style={{ color: '#cbd5e1' }}>{record.platform || '-'} / {record.system_version || '-'}</span>
                            ),
                        },
                        {
                            title: 'IP и гео',
                            key: 'geo',
                            width: 260,
                            render: (_: any, record: any) => (
                                <Space direction="vertical" size={2}>
                                    {record.ip && record.ip !== '-' ? (
                                        <span style={{ color: '#cbd5e1', fontWeight: 600 }}>{record.ip}</span>
                                    ) : record.current ? (
                                        // Telegram IP не отдаёт. Текущая сессия - это наше
                                        // подключение, поэтому показываем адрес из нашей
                                        // проверки: тот, с которого аккаунт выходит в интернет.
                                        <Space size={6} wrap>
                                            <span style={{ color: '#cbd5e1', fontWeight: 600 }}>
                                                {sessionsAccount?.exit_ip || 'IP не проверен'}
                                            </span>
                                            <Button
                                                size="small"
                                                type="link"
                                                style={{ padding: 0, height: 'auto' }}
                                                loading={Boolean(sessionsAccount && exitChecking.has(sessionsAccount.id))}
                                                onClick={() => sessionsAccount && checkExitIp(sessionsAccount.id)}
                                            >
                                                {sessionsAccount?.exit_ip ? 'Обновить' : 'Проверить'}
                                            </Button>
                                        </Space>
                                    ) : (
                                        <span style={{ color: '#cbd5e1', fontWeight: 600 }}>IP не передан Telegram</span>
                                    )}
                                    <span style={{ color: '#94a3b8', fontSize: 12, whiteSpace: 'normal' }}>
                                        {[record.country, record.region].filter((value) => value && value !== '-').join(', ') || 'Гео не передано'}
                                    </span>
                                </Space>
                            ),
                        },
                        {
                            title: 'Активность',
                            key: 'activity',
                            width: 220,
                            render: (_: any, record: any) => (
                                <Space direction="vertical" size={2}>
                                    <span style={{ color: '#cbd5e1' }}>{formatSessionDate(record.date_active)}</span>
                                    <span style={{ color: '#94a3b8', fontSize: 12 }}>создана: {formatSessionDate(record.date_created)}</span>
                                </Space>
                            ),
                        },
                        {
                            title: 'Действия',
                            key: 'actions',
                            fixed: 'right' as const,
                            width: 160,
                            render: (_: any, record: any) => record.current ? (
                                <Tag color="success">Рабочая сессия</Tag>
                            ) : (
                                <Popconfirm
                                    title="Завершить эту Telegram-сессию?"
                                    onConfirm={() => handleRevokeSession(record.hash)}
                                    disabled={sessionsActionLoading}
                                >
                                    <Button danger size="small" loading={sessionsActionLoading}>
                                        Завершить
                                    </Button>
                                </Popconfirm>
                            ),
                        },
                    ]}
                />
            </Modal>

            <Modal
                title={`Чаты аккаунта: ${chatsAccount?.phone || chatsAccount?.username || ''}`}
                open={chatsModalOpen}
                onCancel={closeChatsModal}
                width="100vw"
                style={{ top: 0, maxWidth: '100vw', paddingBottom: 0 }}
                className="account-chats-modal"
                footer={[
                    <Button key="refresh" icon={<ReloadOutlined />} onClick={() => fetchAccountDialogs(undefined, { live: true })} loading={accountDialogsLoading}>
                        Обновить из Telegram
                    </Button>,
                    <Button key="close" onClick={closeChatsModal}>Закрыть</Button>,
                ]}
            >
                <div className="account-chats-layout">
                    <div className="account-chats-list">
                        <Table
                            dataSource={accountDialogs}
                            rowKey={(record) => String(record.db_chat_id || record.chat_id)}
                            loading={accountDialogsLoading}
                            pagination={{ pageSize: 20, size: 'small' }}
                            tableLayout="fixed"
                            scroll={{ y: 'calc(100vh - 270px)' }}
                            rowClassName={(record) => Number(record.db_chat_id || record.chat_id) === Number(selectedAccountDialog?.db_chat_id || selectedAccountDialog?.chat_id) ? 'account-chat-row is-selected' : 'account-chat-row'}
                            onRow={(record) => ({
                                onClick: () => openAccountDialog(record),
                            })}
                            columns={[
                                {
                                    title: 'Чат',
                                    key: 'chat',
                                    width: 260,
                                    render: (_: any, record: any) => (
                                        <Space className="account-chat-cell">
                                            <Avatar src={record.photo_url || record.photo || undefined} icon={<MessageOutlined />} />
                                            <Space direction="vertical" size={2} className="account-chat-title">
                                                <span>{accountDialogTitle(record)}</span>
                                                <small>{record.username ? `@${String(record.username).replace(/^@/, '')}` : record.chat_id}</small>
                                            </Space>
                                        </Space>
                                    ),
                                },
                                {
                                    title: 'Тип',
                                    key: 'type',
                                    width: 96,
                                    render: (_: any, record: any) => <Tag>{record.chat_type || record.type || '-'}</Tag>,
                                },
                                {
                                    title: 'Последнее',
                                    key: 'last',
                                    render: (_: any, record: any) => (
                                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                            <span className="account-chat-last">{record.last_message || '-'}</span>
                                            <small className="account-chat-date">{record.last_message_date ? formatSessionDate(record.last_message_date) : '-'}</small>
                                        </Space>
                                    ),
                                },
                                {
                                    title: 'Непрочитано',
                                    dataIndex: 'unread_count',
                                    key: 'unread_count',
                                    width: 112,
                                    render: (value: number) => value ? <Tag color="blue">{value}</Tag> : '-',
                                },
                                {
                                    title: '',
                                    key: 'actions',
                                    width: 120,
                                    onCell: () => ({
                                        onClick: (event: any) => event.stopPropagation(),
                                    }),
                                    render: (_: any, record: any) => (
                                        <Popconfirm
                                            title="Удалить выбранный чат?"
                                            description="Чат и история будут удалены в CRM и в Telegram у нас и у собеседника."
                                            okText="Удалить везде"
                                            cancelText="Отмена"
                                            onConfirm={() => handleDeleteAccountDialog(record)}
                                        >
                                            <Button
                                                type="text"
                                                danger
                                                size="small"
                                                className="account-chat-delete-btn"
                                                icon={<DeleteOutlined />}
                                                loading={deletingChatId === Number(record.db_chat_id || record.chat_id)}
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                Удалить
                                            </Button>
                                        </Popconfirm>
                                    ),
                                },
                            ]}
                        />
                        {accountDialogsHasMore && (
                            <Button block onClick={loadMoreAccountDialogs} loading={accountDialogsLoading} style={{ marginTop: 8 }}>
                                Загрузить ещё
                            </Button>
                        )}
                    </div>
                    <div className="account-chat-preview">
                        {selectedAccountDialog ? (
                            <>
                                <div className="account-chat-preview-header">
                                    <Space>
                                        <Avatar src={selectedAccountDialog.photo_url || selectedAccountDialog.photo || undefined} icon={<MessageOutlined />} />
                                        <Space direction="vertical" size={0}>
                                            <strong>{accountDialogTitle(selectedAccountDialog)}</strong>
                                            <span>{selectedAccountDialog.username ? `@${String(selectedAccountDialog.username).replace(/^@/, '')}` : selectedAccountDialog.chat_id}</span>
                                        </Space>
                                    </Space>
                                    <Popconfirm
                                        title="Удалить выбранный чат?"
                                        description="Чат и история будут удалены в CRM и в Telegram у нас и у собеседника."
                                        okText="Удалить везде"
                                        cancelText="Отмена"
                                        onConfirm={() => handleDeleteAccountDialog(selectedAccountDialog)}
                                    >
                                        <Button
                                            danger
                                            type="text"
                                            className="account-chat-delete-btn"
                                            icon={<DeleteOutlined />}
                                            loading={deletingChatId === Number(selectedAccountDialog.db_chat_id || selectedAccountDialog.chat_id)}
                                        >
                                            Удалить чат везде
                                        </Button>
                                    </Popconfirm>
                                </div>
                                <Spin spinning={accountDialogMessagesLoading}>
                                    <div className="account-chat-messages">
                                        {accountDialogMessages.length ? accountDialogMessages.map((item) => (
                                            <div className={item.outgoing ? 'account-chat-message outgoing' : 'account-chat-message incoming'} key={`${item.id}-${item.date}`}>
                                                <div className="account-chat-bubble">
                                                    {item.content_type === 'photo' && (
                                                        <img
                                                            src={`/api/accounts/${chatsAccount?.id}/media/${selectedAccountDialog.chat_id}/${item.id}`}
                                                            alt={item.media_file_name || 'Фото'}
                                                        />
                                                    )}
                                                    {item.content_type !== 'photo' && item.content_type !== 'text' && (
                                                        <Tag>{messagePreview(item)}</Tag>
                                                    )}
                                                    {item.text && <div>{item.text}</div>}
                                                    {!item.text && item.content_type === 'text' && <div>{messagePreview(item)}</div>}
                                                    <small>{item.outgoing ? 'Мы' : 'Собеседник'} · {item.date ? formatSessionDate(item.date) : '-'}</small>
                                                </div>
                                            </div>
                                        )) : (
                                            <Empty description="Выберите чат или обновите переписку" />
                                        )}
                                    </div>
                                </Spin>
                                <div className="account-chat-composer">
                                    <Input.TextArea
                                        value={accountDialogText}
                                        onChange={(event) => setAccountDialogText(event.target.value)}
                                        placeholder="Сообщение..."
                                        autoSize={{ minRows: 1, maxRows: 4 }}
                                        disabled={accountDialogSending}
                                        onPressEnter={(event) => {
                                            if (!event.shiftKey) {
                                                event.preventDefault()
                                                handleSendAccountDialogMessage()
                                            }
                                        }}
                                    />
                                    <Button
                                        type="primary"
                                        icon={<SendOutlined />}
                                        loading={accountDialogSending}
                                        disabled={!accountDialogText.trim()}
                                        onClick={handleSendAccountDialogMessage}
                                    />
                                </div>
                            </>
                        ) : (
                            <Empty description="Выберите чат слева для просмотра переписки" />
                        )}
                    </div>
                </div>
            </Modal>

            {contextHolder}
        </div>
    )
}

function ProxiesPage() {
    const [proxies, setProxies] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [proxyCheckResult, setProxyCheckResult] = useState<any>(null)
    const [proxyCheckModalOpen, setProxyCheckModalOpen] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingProxy, setEditingProxy] = useState<any>(null)
    const [bulkModalOpen, setBulkModalOpen] = useState(false)
    const [bulkForm] = Form.useForm()
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [form] = Form.useForm()
    const [editForm] = Form.useForm()
    const { user } = useAuthStore()
    const isTeamLead = seesAllLeads(user?.role)

    useEffect(() => {
        fetchProxies()
    }, [])

    const fetchProxies = async () => {
        try {
            const response = await proxiesApi.getAll()
            setProxies(response.data)
        } catch (error) {
            message.error('Ошибка загрузки прокси')
        } finally {
            setLoading(false)
        }
    }

    const handleCreate = async (values: any) => {
        try {
            await proxiesApi.create(values)
            message.success('Прокси добавлен')
            setModalOpen(false)
            form.resetFields()
            fetchProxies()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка создания')
        }
    }

    const handleBulkCreate = async (values: any) => {
        try {
            const response = await proxiesApi.bulkCreate(values)
            const { created, failed, results } = response.data
            if (failed) {
                // Какие строки не добавились и почему - иначе непонятно, что исправлять.
                const errors = (results || []).filter((item: any) => !item.success)
                Modal.warning({
                    title: `Добавлено: ${created}, не добавлено: ${failed}`,
                    width: 640,
                    content: (
                        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                            {errors.map((item: any) => (
                                <div key={item.line}>Строка {item.line}: {item.error}</div>
                            ))}
                        </div>
                    ),
                })
            } else {
                message.success(`Прокси добавлено: ${created}`)
            }
            setBulkModalOpen(false)
            bulkForm.resetFields()
            fetchProxies()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка массового добавления прокси')
        }
    }

    const handleCheck = async (id: number) => {
        const hide = message.loading('Проверка прокси...', 0)
        try {
            const response = await proxiesApi.check(id)
            hide()
            setProxyCheckResult(response.data)
            setProxyCheckModalOpen(true)
            fetchProxies()
        } catch (error: any) {
            hide()
            message.error(error.response?.data?.detail || 'Ошибка проверки')
        }
    }

    const handleEdit = (record: any) => {
        setEditingProxy(record)
        editForm.setFieldsValue({
            name: record.name,
            type: record.type,
            host: record.host,
            port: record.port,
            username: record.username,
            password: record.password,
            country: record.country,
            is_mobile: record.is_mobile,
            status: record.status,
        })
        setEditModalOpen(true)
    }

    const handleUpdate = async (values: any) => {
        try {
            await proxiesApi.update(editingProxy.id, values)
            message.success('Прокси обновлён')
            setEditModalOpen(false)
            editForm.resetFields()
            fetchProxies()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка обновления')
        }
    }

    const handleDelete = async (id: number) => {
        try {
            await proxiesApi.delete(id)
            message.success('Прокси удалён')
            fetchProxies()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка удаления')
        }
    }

    const columns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            // Сортировка по всем колонкам - на месте, по загруженному списку.
            // Пустые значения при сортировке по возрастанию уходят вниз.
            sorter: (a: any, b: any) => naturalCompare(a.name, b.name) || a.id - b.id,
        },
        {
            title: 'Тип',
            dataIndex: 'type',
            key: 'type',
            sorter: (a: any, b: any) => naturalCompare(a.type, b.type) || naturalCompare(a.name, b.name),
            render: (type: string) => <Tag>{type.toUpperCase()}</Tag>,
        },
        {
            title: 'Адрес',
            key: 'address',
            // Сначала хост, затем порт числом: :1000 раньше :1002.
            sorter: (a: any, b: any) => naturalCompare(a.host, b.host)
                || (Number(a.port) || 0) - (Number(b.port) || 0)
                || naturalCompare(a.name, b.name),
            render: (_: any, record: any) => `${record.host}:${record.port}`,
        },
        {
            title: 'Страна',
            dataIndex: 'country',
            key: 'country',
            sorter: (a: any, b: any) => naturalCompare(a.country, b.country) || naturalCompare(a.name, b.name),
            render: (country: string) => country || '-',
        },
        {
            title: 'Мобильный',
            dataIndex: 'is_mobile',
            key: 'is_mobile',
            // По возрастанию мобильные идут первыми.
            sorter: (a: any, b: any) => Number(Boolean(b.is_mobile)) - Number(Boolean(a.is_mobile))
                || naturalCompare(a.name, b.name),
            render: (v: boolean) => (v ? <Tag color="success">Да</Tag> : '-'),
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            sorter: (a: any, b: any) => naturalCompare(a.status, b.status) || naturalCompare(a.name, b.name),
            render: (status: string) => (
                <Tag color={status === 'active' ? 'success' : status === 'error' ? 'error' : 'default'}>
                    {status}
                </Tag>
            ),
        },
    ]

    if (isTeamLead) {
        columns.push({
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        type="text"
                        icon={<ReloadOutlined />}
                        onClick={() => handleCheck(record.id)}
                        title="Проверить"
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        title="Редактировать"
                    />
                    <Popconfirm
                        title="Удалить прокси?"
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} title="Удалить" />
                    </Popconfirm>
                </Space>
            ),
        } as any)
    }

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h1 style={{ color: 'white', margin: 0 }}>Прокси</h1>
                <Space>
                    <Button
                        icon={<PlusOutlined />}
                        onClick={() => setBulkModalOpen(true)}
                    >
                        Массово
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setModalOpen(true)}
                    >
                        Добавить
                    </Button>
                </Space>
            </div>

            <Table
                dataSource={proxies}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title="Новый прокси"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
            >
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Название"
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="Proxy RU Mobile 1" />
                    </Form.Item>
                    <Form.Item name="type" label="Тип" initialValue="socks5">
                        <Select>
                            <Select.Option value="socks5">SOCKS5</Select.Option>
                            <Select.Option value="http">HTTP</Select.Option>
                            <Select.Option value="https">HTTPS</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="host"
                        label="Хост"
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="proxy.example.com" />
                    </Form.Item>
                    <Form.Item
                        name="port"
                        label="Порт"
                        rules={[{ required: true }]}
                    >
                        <Input type="number" placeholder="1080" />
                    </Form.Item>
                    <Form.Item name="username" label="Логин">
                        <Input />
                    </Form.Item>
                    <Form.Item name="password" label="Пароль">
                        <Input.Password />
                    </Form.Item>
                    <Form.Item name="country" label="Гео">
                        <Select allowClear placeholder="Выберите гео" options={GEO_OPTIONS.map((geo) => ({ value: geo, label: geo }))} />
                    </Form.Item>
                    <Form.Item name="is_mobile" valuePropName="checked" label="">
                        <Select defaultValue={false}>
                            <Select.Option value={false}>Не мобильный</Select.Option>
                            <Select.Option value={true}>Мобильный</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>


            <Modal
                title="Массовое добавление прокси"
                open={bulkModalOpen}
                onCancel={() => setBulkModalOpen(false)}
                onOk={() => bulkForm.submit()}
                width={680}
            >
                <Form form={bulkForm} onFinish={handleBulkCreate} layout="vertical" initialValues={{ type: 'socks5', is_mobile: false }}>
                    <Form.Item name="geo" label="Гео" rules={[{ required: true, message: 'Выберите гео' }]}>
                        <Select placeholder="Выберите гео" options={GEO_OPTIONS.map((geo) => ({ value: geo, label: geo }))} />
                    </Form.Item>
                    <Form.Item name="type" label="Тип по умолчанию">
                        <Select>
                            <Select.Option value="socks5">SOCKS5</Select.Option>
                            <Select.Option value="http">HTTP</Select.Option>
                            <Select.Option value="https">HTTPS</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="is_mobile" label="Тип прокси">
                        <Select>
                            <Select.Option value={false}>Не мобильный</Select.Option>
                            <Select.Option value={true}>Мобильный</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="proxies_text"
                        label="Прокси"
                        rules={[{ required: true, message: 'Вставьте список прокси' }]}
                        extra="По одному прокси в строке. Форматы: host:port, host:port:login:password, login:password@host:port, socks5://login:password@host:port. Прокси с одним адресом и разными логинами или паролями добавляются как отдельные."
                    >
                        <Input.TextArea rows={10} placeholder={"1.2.3.4:1080\n1.2.3.5:1080:user:pass"} />
                    </Form.Item>
                </Form>
            </Modal>


            <Modal
                title="Редактировать прокси"
                open={editModalOpen}
                onCancel={() => setEditModalOpen(false)}
                onOk={() => editForm.submit()}
            >
                <Form form={editForm} onFinish={handleUpdate} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Название"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="type" label="Тип">
                        <Select>
                            <Select.Option value="socks5">SOCKS5</Select.Option>
                            <Select.Option value="http">HTTP</Select.Option>
                            <Select.Option value="https">HTTPS</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="host"
                        label="Хост"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="port"
                        label="Порт"
                        rules={[{ required: true }]}
                    >
                        <Input type="number" />
                    </Form.Item>
                    <Form.Item name="username" label="Логин">
                        <Input />
                    </Form.Item>
                    <Form.Item name="password" label="Пароль">
                        <Input.Password />
                    </Form.Item>
                    <Form.Item name="country" label="Страна">
                        <Input />
                    </Form.Item>
                    <Form.Item name="is_mobile" valuePropName="checked" label="">
                        <Select>
                            <Select.Option value={false}>Не мобильный</Select.Option>
                            <Select.Option value={true}>Мобильный</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="status" label="Статус">
                        <Select>
                            <Select.Option value="active">Активен</Select.Option>
                            <Select.Option value="error">Ошибка</Select.Option>
                            <Select.Option value="inactive">Неактивен</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>


            <Modal
                title={proxyCheckResult?.success ? 'Прокси работает' : 'Ошибка прокси'}
                open={proxyCheckModalOpen}
                onCancel={() => setProxyCheckModalOpen(false)}
                footer={[
                    <Button key="ok" type="primary" onClick={() => setProxyCheckModalOpen(false)}>
                        OK
                    </Button>
                ]}
            >
                {proxyCheckResult && (
                    <div style={{ marginTop: 16 }}>
                        {proxyCheckResult.success ? (
                            <>
                                <p style={{ marginBottom: 8, fontSize: 16 }}>Внешний IP: <b style={{ color: '#52c41a' }}>{proxyCheckResult.ip}</b></p>
                                <p style={{ color: '#94a3b8' }}>Прокси: {proxyCheckResult.proxy}</p>
                            </>
                        ) : (
                            <p style={{ color: '#ff4d4f', fontSize: 16 }}>{proxyCheckResult.message}</p>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    )
}
